import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate, useLocation } from 'react-router-dom'
import { STRINGS } from '../constants/strings'
import { useAuth } from '../hooks'
import { api } from '../data/api'
import ConfirmModal from '../components/confirmModal'
import CreateExamForm from '../components/createExamForm'
import ExamDetail from '../components/examDetail'
import { shareOrigin } from '../utils/shareLink'

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

function fmtCredits(n) {
  return `₹${(n ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
}

// ── Credits (shared by the Organisations and Examinees tabs) ────────────────────

// balance null means this account type doesn't carry a real balance (an
// org-linked examinee — their org's balance is what matters instead).
// Plain absolute-balance color cue — not tied to the purchase-history-based
// percentage used for the actual low-credits email (that's a per-account
// lookup, too expensive to do per row in a list of many accounts).
function CreditsBadge({ balance }) {
  if (balance == null) return <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
  const color = balance <= 0 ? '#f87171' : balance < 100 ? '#fbbf24' : '#34d399'
  return <span className="text-xs font-bold" style={{ color }}>{fmtCredits(balance)}</span>
}

function TopUpModal({ target, onClose, onSaved, showToast }) {
  const [amount, setAmount] = useState(String(target.creditsBalance ?? 0))
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    const n = Number(amount)
    if (amount === '' || Number.isNaN(n)) return
    setSaving(true)
    try {
      const updated = await api.patch(`/api/platform-settings/accounts/${target._id}`, { mode: 'set', amount: n })
      showToast(`${target.name}'s credits updated.`)
      onSaved(updated)
    } catch (err) {
      showToast(err.message || 'Failed to update credits.', 'error')
    } finally {
      setSaving(false)
    }
  }

  const current = target.creditsBalance ?? 0

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="card p-6 shadow-2xl animate-fade-up w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--brand)' }}>Update Credits</p>
        <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-1)' }}>{target.name}</p>
        <p className="text-xs mb-4" style={{ color: 'var(--text-3)' }}>Current balance: <span className="font-semibold" style={{ color: 'var(--text-1)' }}>{fmtCredits(current)}</span></p>

        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-2)' }}>New credits balance (₹)</label>
        <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
          className="w-full px-3 py-2 rounded-xl text-sm outline-none mb-4"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
          autoFocus />

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-2 rounded-xl text-sm font-semibold"
            style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Cancel</button>
          <button onClick={handleSave} disabled={amount === '' || saving} className="flex-1 py-2 rounded-xl text-sm font-semibold disabled:opacity-40"
            style={{ background: 'var(--brand)', color: '#fff' }}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Toast ─────────────────────────────────────────────────────────────────────

function Toast({ toast }) {
  if (!toast) return null
  const styles = {
    error:   { border: 'border-red-500/40',    text: 'text-red-400',     bg: '#3b0d0d', icon: '✗' },
    info:    { border: 'border-sky-500/40',     text: 'text-sky-300',     bg: '#0c1e2e', icon: '⏳' },
    success: { border: 'border-emerald-500/40', text: 'text-emerald-300', bg: '#0d2b1f', icon: '✓' },
  }
  const s = styles[toast.type] ?? styles.success
  return (
    <div className={`fixed top-16 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-4 py-3 rounded-xl border text-sm shadow-2xl animate-fade-in max-w-sm text-center ${s.border} ${s.text}`}
         style={{ background: s.bg }}>
      <span className="flex-shrink-0">{s.icon}</span>
      <span>{toast.msg}</span>
    </div>
  )
}

function QuestionStatusBadge({ status }) {
  if (!status || status === 'ready') return null
  const cfg = {
    pending:    { label: 'Pending',     bg: 'rgba(148,163,184,0.12)', color: '#94a3b8' },
    generating: { label: 'Generating…', bg: 'rgba(251,191,36,0.12)',  color: '#fbbf24' },
    failed:     { label: 'Failed',      bg: 'rgba(248,113,113,0.1)',  color: '#f87171' },
  }
  const c = cfg[status] ?? cfg.pending
  return (
    <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: c.bg, color: c.color }}>
      {status === 'generating' && <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: c.color }} />}
      {c.label}
    </span>
  )
}

// ── Org Detail ────────────────────────────────────────────────────────────────

function InviteCodeChip({ code, orgName }) {
  const [copied, setCopied] = useState(false)
  if (!code) return null

  function handleCopy() {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  // Wording lives in strings.js (S.inviteCodeMessage) — single source of
  // truth shared with the org's own InviteCodeChip (adminPage.jsx), instead
  // of a hand-copied duplicate. shareOrigin() always points at the branded
  // skillassess.radtar.com on prod; dev keeps its own URL.
  const shareText = STRINGS.admin.inviteCodeMessage(orgName, shareOrigin(), code)
  const waUrl     = `https://wa.me/?text=${encodeURIComponent(shareText)}`
  const mailUrl   = `mailto:?subject=${encodeURIComponent(STRINGS.admin.inviteCodeSubject(orgName))}&body=${encodeURIComponent(shareText)}`

  return (
    <div className="mt-1">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>Invite Code</span>
        <span className="font-mono text-sm font-bold tracking-widest px-2.5 py-0.5 rounded-lg"
              style={{ background: 'var(--surface-2)', color: 'var(--brand)', border: '1px solid var(--border)' }}>
          {code}
        </span>
        <button onClick={handleCopy}
          className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-lg transition-all"
          style={{ background: copied ? 'rgba(52,211,153,0.1)' : 'var(--surface-2)', border: `1px solid ${copied ? 'rgba(52,211,153,0.3)' : 'var(--border)'}`, color: copied ? '#34d399' : 'var(--text-3)' }}>
          {copied ? '✓ Copied' : '⎘ Copy'}
        </button>
        <a href={waUrl} target="_blank" rel="noreferrer"
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#25d366', textDecoration: 'none' }}
          title="Share via WhatsApp">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
        </a>
        <a href={mailUrl}
          className="w-6 h-6 rounded-md flex items-center justify-center transition-all"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: '#f87171', textDecoration: 'none' }}
          title="Share via Email">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
        </a>
      </div>
    </div>
  )
}

function OrgDetail({ org, onBack, onUpdated, showToast }) {
  const [users,      setUsers]      = useState(null)
  const [total,      setTotal]      = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search,     setSearch]     = useState('')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(10)
  // Disabled — feature access is now governed entirely by PlatformSettings.
  // featureUnlockPrices (Settings tab), not a per-org manual grant.
  // const [permissions,   setPermissions]   = useState(org.examPermissions ?? DEFAULT_EXAM_PERMISSIONS)
  // const [savingPerms,   setSavingPerms]   = useState(false)

  function fetchUsers(pg = page, limit = perPage, q = search) {
    setUsers(null)
    const params = new URLSearchParams({ page: pg, limit, ...(q ? { search: q } : {}) })
    api.get(`/api/orgs/${org._id}/users?${params}`)
      .then(data => { setUsers(data.users); setTotal(data.total); setTotalPages(data.totalPages) })
      .catch(() => { setUsers([]); setTotal(0); setTotalPages(1) })
  }

  useEffect(() => { fetchUsers() }, [org._id])

  // async function handleSavePermissions() {
  //   setSavingPerms(true)
  //   try {
  //     const updated = await api.patch(`/api/orgs/${org._id}/permissions`, { examPermissions: permissions })
  //     onUpdated?.(updated)
  //     showToast?.('Permissions updated.')
  //   } catch (err) {
  //     showToast?.(err.message, 'error')
  //   } finally {
  //     setSavingPerms(false)
  //   }
  // }

  return (
    <div className="animate-fade-up">
      <button onClick={onBack} className="btn-ghost mb-6 text-sm">← All Organisations</button>

      {/* Org header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl font-black flex-shrink-0"
             style={{ background: 'var(--brand)', color: '#fff' }}>
          {org.name[0].toUpperCase()}
        </div>
        <div>
          <h2 className="text-xl font-black" style={{ color: 'var(--text-1)' }}>{org.name}</h2>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{org.email}</p>
          <InviteCodeChip code={org.inviteCode} orgName={org.name} />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {[
          { v: org.examCount,  l: 'Exams',   c: 'var(--brand)' },
          { v: org.userCount,  l: 'Users',   c: '#34d399' },
          { v: fmtDate(org.createdAt), l: 'Joined', c: 'var(--text-1)' },
        ].map(({ v, l, c }) => (
          <div key={l} className="card p-4 text-center">
            <div className="font-bold text-lg leading-tight" style={{ color: c }}>{v}</div>
            <div className="text-[0.7rem] text-[var(--text-3)] mt-0.5">{l}</div>
          </div>
        ))}
      </div>

      {/* Exam Permissions — disabled, see PlatformSettingsView's per-feature pricing instead */}
      {/* <div className="card p-4 mb-6">
        <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-3)' }}>Exam Permissions</p>
        <PermissionsFields permissions={permissions} onChange={setPermissions} />
        <div className="flex justify-end mt-3">
          <button onClick={handleSavePermissions} disabled={savingPerms} className="btn-primary text-xs px-4 py-2 disabled:opacity-50">
            {savingPerms ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Saving…</> : 'Save Permissions'}
          </button>
        </div>
      </div> */}

      {/* Users section */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative w-full sm:w-56">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
          <input type="text" value={search}
            onChange={e => { const q = e.target.value; setSearch(q); setPage(1); fetchUsers(1, perPage, q) }}
            placeholder="Search users…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
            onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
            onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
          {search && (
            <button onClick={() => { setSearch(''); setPage(1); fetchUsers(1, perPage, '') }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>✕</button>
          )}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>{total} user{total !== 1 ? 's' : ''}</span>
        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
          <select value={perPage} onChange={e => { const l = Number(e.target.value); setPerPage(l); setPage(1); fetchUsers(1, l) }}
            className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
      </div>

      {!users ? (
        <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>
      ) : total === 0 && !search ? (
        <div className="card p-10 text-center">
          <p className="text-3xl mb-2">👥</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>No users enrolled yet.</p>
        </div>
      ) : (
        <>
          {/* Mobile user cards */}
          <div className="sm:hidden space-y-3">
            {users.map(u => (
              <div key={u._id} className="card p-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                       style={{ background: 'var(--brand)', color: '#fff' }}>
                    {u.name?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                  </div>
                </div>
                <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1">
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>Joined: {fmtDate(u.createdAt)}</span>
                  {u.phone?.number && (
                    <span className="text-xs" style={{ color: 'var(--text-3)' }}>{u.phone.countryCode} {u.phone.number}</span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block card overflow-hidden">
            <div className="grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                 style={{ gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <span>User</span><span>Joined</span><span>Mobile</span>
              <span className="text-center">{STRINGS.modes.virtual}</span>
              {/* <span className="text-center">{STRINGS.modes.practice}</span> */}
            </div>
            {users.map(u => (
              <div key={u._id} className="grid px-6 py-4 items-center border-b last:border-0"
                   style={{ gridTemplateColumns: '2fr 1.2fr 1.2fr 1fr', borderColor: 'var(--border)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                       style={{ background: 'var(--brand)', color: '#fff' }}>
                    {u.name?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                    <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                  </div>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(u.createdAt)}</p>
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>{u.phone?.number ? `${u.phone.countryCode} ${u.phone.number}` : '—'}</p>
                <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>{u.examsTaken ?? 0}</p>
                {/* <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>—</p> */}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-5">
              <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
              </p>
              <div className="flex items-center gap-1">
                <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchUsers(p) }} disabled={page === 1}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>← Prev</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                  <button key={n} onClick={() => { setPage(n); fetchUsers(n) }}
                    className="w-8 h-8 rounded-lg text-xs font-semibold transition-all"
                    style={n === page
                      ? { background: 'var(--brand)', color: '#fff' }
                      : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                    {n}
                  </button>
                ))}
                <button onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchUsers(p) }} disabled={page === totalPages}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Next →</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  approved: { bg: 'rgba(52,211,153,0.12)', color: '#34d399', dot: '#34d399', label: 'Approved' },
  pending:  { bg: 'rgba(251,191,36,0.12)', color: '#fbbf24', dot: '#fbbf24', label: 'Pending'  },
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.pending
  return (
    <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
          style={{ background: s.bg, color: s.color }}>
      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: s.dot }} />
      {s.label}
    </span>
  )
}

// ── Exam Permissions (per-organisation feature gate) ───────────────────────────

const PERMISSION_FIELDS = [
  { key: 'mcq',            label: 'MCQ' },
  { key: 'msq',             label: 'MSQ' },
  { key: 'true_false',      label: 'True / False' },
  { key: 'short_answer',    label: 'Short Answer' },
  { key: 'bloomsTaxonomy',  label: "Bloom's Taxonomy" },
]

const DEFAULT_EXAM_PERMISSIONS = { mcq: true, true_false: true, short_answer: false, msq: false, bloomsTaxonomy: false }

function PermissionCheckbox({ label, checked, onChange }) {
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className="flex items-center gap-2 px-3 py-2 rounded-xl border text-left transition-all"
      style={{ background: checked ? 'var(--surface-2)' : 'var(--surface)', borderColor: checked ? 'var(--brand)' : 'var(--border)' }}>
      <span className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0"
        style={{ background: checked ? 'var(--brand)' : 'var(--surface-2)', border: `1.5px solid ${checked ? 'var(--brand)' : 'var(--border)'}` }}>
        {checked && <svg viewBox="0 0 10 8" className="w-2.5 h-2.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 4l2.5 2.5L9 1"/></svg>}
      </span>
      <span className="text-xs font-medium" style={{ color: 'var(--text-1)' }}>{label}</span>
    </button>
  )
}

function PermissionsFields({ permissions, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
      {PERMISSION_FIELDS.map(({ key, label }) => (
        <PermissionCheckbox key={key} label={label} checked={!!permissions[key]}
          onChange={v => onChange({ ...permissions, [key]: v })} />
      ))}
    </div>
  )
}

// ── Add Organisation Form ─────────────────────────────────────────────────────

function AddOrgForm({ onCreated, onCancel }) {
  const [name,    setName]    = useState('')
  const [email,   setEmail]   = useState('')
  const [password,setPassword]= useState('')
  // Disabled — see OrgDetail's commented-out permissions panel above.
  // const [examPermissions, setExamPermissions] = useState(DEFAULT_EXAM_PERMISSIONS)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim() || !email.trim() || !password.trim()) return
    setSaving(true)
    setError('')
    try {
      const org = await api.post('/api/orgs', { name, email, password })
      onCreated(org)
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="card p-5 mb-5 animate-fade-up" style={{ borderColor: 'var(--brand)', borderWidth: '1px' }}>
      <h3 className="text-sm font-bold mb-4" style={{ color: 'var(--text-1)' }}>Add Organisation</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {[
          { label: 'Name *',     value: name,     set: setName,     type: 'text',     placeholder: 'Organisation name', autoComplete: 'off' },
          { label: 'Email *',    value: email,    set: setEmail,    type: 'email',    placeholder: 'admin@org.com',     autoComplete: 'off' },
          { label: 'Password *', value: password, set: setPassword, type: 'password', placeholder: 'password',          autoComplete: 'new-password' },
        ].map(({ label, value, set, type, placeholder, autoComplete }) => (
          <div key={label}>
            <label className="text-xs font-medium text-[var(--text-3)] mb-1 block">{label}</label>
            <input type={type} value={value} onChange={e => set(e.target.value)} placeholder={placeholder} autoComplete={autoComplete}
              className="w-full px-3 py-2.5 rounded-xl text-sm bg-[var(--surface-2)] border border-[var(--border)] text-[var(--text-1)] placeholder:text-[var(--text-3)] focus:outline-none focus:border-[var(--brand)] transition" />
          </div>
        ))}
      </div>
      {/* <div className="mb-4">
        <label className="text-xs font-medium text-[var(--text-3)] mb-1.5 block">Exam Permissions</label>
        <PermissionsFields permissions={examPermissions} onChange={setExamPermissions} />
      </div> */}
      {error && <p className="text-xs text-red-400 mb-3">{error}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onCancel} className="btn-ghost text-xs px-4 py-2">Cancel</button>
        <button type="submit" disabled={saving || !name.trim() || !email.trim() || !password.trim()} className="btn-primary text-xs px-4 py-2 disabled:opacity-50">
          {saving ? <><span className="w-3 h-3 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Adding…</> : 'Add Organisation'}
        </button>
      </div>
    </form>
  )
}

// ── Organisations Tab ─────────────────────────────────────────────────────────

function OrgsView({ onSelectOrg, showToast }) {
  const [orgs,       setOrgs]       = useState(null)
  const [total,      setTotal]      = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search,     setSearch]     = useState('')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(10)
  const [adding,     setAdding]     = useState(false)
  const [confirm,        setConfirm]        = useState(null)
  const [approveConfirm, setApproveConfirm] = useState(null)
  const [topUpTarget,    setTopUpTarget]    = useState(null)

  function fetchOrgs(pg = page, limit = perPage, q = search) {
    setOrgs(null)
    const params = new URLSearchParams({ page: pg, limit, ...(q ? { search: q } : {}) })
    api.get(`/api/orgs?${params}`)
      .then(data => { setOrgs(data.orgs); setTotal(data.total); setTotalPages(data.totalPages) })
      .catch(() => { setOrgs([]); setTotal(0); setTotalPages(1) })
  }

  useEffect(() => { fetchOrgs() }, [])

  async function handleApprove() {
    const org = approveConfirm
    setApproveConfirm(null)
    try {
      await api.patch(`/api/orgs/${org._id}/approve`)
      showToast(`${org.name} approved.`)
      fetchOrgs()
    } catch (err) {
      showToast(err.message, 'error')
    }
  }

  async function handleToggleEnabled() {
    const { org, enabled } = confirm
    setConfirm(null)
    try {
      await api.patch(`/api/orgs/${org._id}/toggle`, { enabled })
      showToast(`${org.name} ${enabled ? 'enabled' : 'disabled'}.`)
      fetchOrgs()
    } catch (err) {
      showToast(err.message, 'error')
    }
  }

  function handleCreated(org) {
    setAdding(false)
    showToast(`${org.name} added successfully.`)
    fetchOrgs(1)
    setPage(1)
  }

  if (!orgs) return <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>

  return (
    <>
      {approveConfirm && (
        <ConfirmModal
          title={`Approve ${approveConfirm.name}?`}
          message={`${approveConfirm.name} will be approved and they'll receive an email notification to log in.`}
          confirmLabel="Approve"
          cancelLabel="Cancel"
          onConfirm={handleApprove}
          onCancel={() => setApproveConfirm(null)}
        />
      )}
      {confirm && (
        <ConfirmModal
          title={confirm.enabled ? `Enable ${confirm.org.name}?` : `Disable ${confirm.org.name}?`}
          message={confirm.enabled
            ? `${confirm.org.name} will be enabled. They can log in and access their dashboard.`
            : `${confirm.org.name} will be disabled. They won't be able to log in until re-enabled.`}
          confirmLabel={confirm.enabled ? 'Enable' : 'Disable'}
          cancelLabel="Cancel"
          danger={!confirm.enabled}
          onConfirm={handleToggleEnabled}
          onCancel={() => setConfirm(null)}
        />
      )}
      {adding && <AddOrgForm onCreated={handleCreated} onCancel={() => setAdding(false)} />}
      {topUpTarget && (
        <TopUpModal target={topUpTarget} showToast={showToast} onClose={() => setTopUpTarget(null)}
          onSaved={updated => {
            setOrgs(prev => prev.map(o => o._id === updated._id ? { ...o, creditsBalance: updated.creditsBalance } : o))
            setTopUpTarget(null)
          }} />
      )}

      <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative w-full sm:w-56">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
          <input type="text" value={search}
            onChange={e => { const q = e.target.value; setSearch(q); setPage(1); fetchOrgs(1, perPage, q) }}
            placeholder="Search organisations…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
            onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
            onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
          {search && <button onClick={() => { setSearch(''); setPage(1); fetchOrgs(1, perPage, '') }} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>✕</button>}
        </div>
        <span className="text-xs" style={{ color: 'var(--text-3)' }}>{total} organisation{total !== 1 ? 's' : ''}</span>
        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
          <select value={perPage} onChange={e => { const l = Number(e.target.value); setPerPage(l); setPage(1); fetchOrgs(1, l) }}
            className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
            {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          {!adding && (
            <button onClick={() => setAdding(true)} className="btn-primary text-xs">+ Add Organisation</button>
          )}
        </div>
      </div>

      {total === 0 && !search && !adding ? (
        <div className="card p-10 text-center">
          <p className="text-4xl mb-3">🏢</p>
          <p className="text-base font-semibold mb-1" style={{ color: 'var(--text-1)' }}>No organisations yet</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>Organisations appear here when they register or you add one.</p>
        </div>
      ) : (
        <>
          {/* Mobile org cards */}
          <div className="sm:hidden space-y-3">
            {orgs.map(org => (
              <div key={org._id} className="card p-4 cursor-pointer active:opacity-80"
                   onClick={() => onSelectOrg(org)}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black flex-shrink-0"
                         style={{ background: 'var(--brand)', color: '#fff' }}>
                      {org.name[0].toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{org.name}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{org.email}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <StatusBadge status={org.status ?? 'pending'} />
                    {org.status === 'approved' && (
                      <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                            style={org.enabled
                              ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                              : { background: 'rgba(248,113,113,0.1)', color: '#f87171' }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: org.enabled ? '#34d399' : '#f87171' }} />
                        {org.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs" style={{ color: 'var(--text-3)' }}>Code:</span>
                    <button className="font-mono text-xs font-bold" style={{ color: 'var(--brand)' }}
                      onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(org.inviteCode) }}>
                      {org.inviteCode}
                    </button>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>{org.examCount} exam{org.examCount !== 1 ? 's' : ''}</span>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>{org.userCount} user{org.userCount !== 1 ? 's' : ''}</span>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>Joined {fmtDate(org.createdAt)}</span>
                  <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-3)' }}>
                    Credits: <CreditsBadge balance={org.creditsBalance} />
                  </span>
                </div>

                <div className="flex items-center justify-end gap-2" onClick={e => e.stopPropagation()}>
                  {org.status === 'approved' ? (
                    <button title={org.enabled ? 'Disable organisation' : 'Enable organisation'}
                      onClick={() => setConfirm({ org, enabled: !org.enabled })}
                      className="relative flex-shrink-0" style={{ width: '36px', height: '20px' }}>
                      <div className="w-full h-full rounded-full transition-all duration-200"
                           style={{ background: org.enabled ? '#34d399' : 'var(--surface-2)', border: `1.5px solid ${org.enabled ? '#34d399' : 'var(--border)'}` }} />
                      <div className="absolute top-[3px] rounded-full transition-all duration-200"
                           style={{ width: '14px', height: '14px', background: org.enabled ? '#fff' : 'var(--text-3)', left: org.enabled ? '19px' : '3px' }} />
                    </button>
                  ) : (
                    <button title="Approve organisation"
                      onClick={() => setApproveConfirm(org)}
                      className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg"
                      style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}>
                      <svg viewBox="0 0 16 16" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.5 8.5l3.5 3.5 7-7" />
                      </svg>
                      Approve
                    </button>
                  )}
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    title="Top up credits"
                    onClick={e => { e.stopPropagation(); setTopUpTarget(org) }}>
                    💰
                  </button>
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    onClick={e => { e.stopPropagation(); onSelectOrg(org) }}>
                    <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block card overflow-x-auto">
            <div style={{ minWidth: '980px' }}>
            <div className="grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                 style={{ gridTemplateColumns: '1.2fr 1.2fr 0.7fr 0.5fr 0.5fr 0.7fr 90px 90px 90px 130px', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <span>Organisation</span>
              <span>Email</span>
              <span>Invite Code</span>
              <span className="text-center">Exams</span>
              <span className="text-center">Users</span>
              <span>Credits</span>
              <span>Joined</span>
              <span className="text-center">Status</span>
              <span className="text-center leading-tight">Enable/<br/>Disable</span>
              <span className="text-center">Actions</span>
            </div>
            {orgs.map(org => (
              <div key={org._id}
                   className="grid px-6 py-4 items-center border-b last:border-0 cursor-pointer transition-colors"
                   style={{ gridTemplateColumns: '1.2fr 1.2fr 0.7fr 0.5fr 0.5fr 0.7fr 90px 90px 90px 130px', borderColor: 'var(--border)' }}
                   onClick={() => onSelectOrg(org)}
                   onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                   onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                       style={{ background: 'var(--brand)', color: '#fff' }}>
                    {org.name[0].toUpperCase()}
                  </div>
                  <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{org.name}</p>
                </div>
                <p className="text-sm truncate" style={{ color: 'var(--text-3)' }}>{org.email}</p>
                <button
                  className="flex items-center gap-1.5 group"
                  title="Copy invite code"
                  onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(org.inviteCode) }}
                >
                  <span className="font-mono text-sm font-bold" style={{ color: 'var(--brand)' }}>{org.inviteCode}</span>
                  <svg viewBox="0 0 16 16" className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--brand)' }}>
                    <rect x="5" y="5" width="9" height="9" rx="1.5"/><path d="M3 11V3a1 1 0 0 1 1-1h8"/>
                  </svg>
                </button>
                <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>{org.examCount}</p>
                <p className="text-sm font-bold text-center" style={{ color: 'var(--text-1)' }}>{org.userCount}</p>
                <CreditsBadge balance={org.creditsBalance} />
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(org.createdAt)}</p>

                <div className="flex justify-center">
                  <StatusBadge status={org.status ?? 'pending'} />
                </div>

                <div className="flex justify-center">
                  {org.status === 'approved' ? (
                    <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                          style={org.enabled
                            ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                            : { background: 'rgba(248,113,113,0.1)', color: '#f87171' }}>
                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                            style={{ background: org.enabled ? '#34d399' : '#f87171' }} />
                      {org.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  ) : (
                    <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
                  )}
                </div>

                <div className="flex items-center justify-center gap-1.5" onClick={e => e.stopPropagation()}>
                  {org.status === 'approved' && (
                    <button
                      title={org.enabled ? 'Disable organisation' : 'Enable organisation'}
                      onClick={() => setConfirm({ org, enabled: !org.enabled })}
                      className="relative flex-shrink-0"
                      style={{ width: '36px', height: '20px' }}>
                      <div className="w-full h-full rounded-full transition-all duration-200"
                           style={{ background: org.enabled ? '#34d399' : 'var(--surface-2)', border: `1.5px solid ${org.enabled ? '#34d399' : 'var(--border)'}` }} />
                      <div className="absolute top-[3px] rounded-full transition-all duration-200"
                           style={{ width: '14px', height: '14px', background: org.enabled ? '#fff' : 'var(--text-3)', left: org.enabled ? '19px' : '3px' }} />
                    </button>
                  )}
                  {org.status !== 'approved' && (
                    <button
                      title="Approve organisation"
                      onClick={() => setApproveConfirm(org)}
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                      style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.18)' }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.08)' }}>
                      <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.5 8.5l3.5 3.5 7-7" />
                      </svg>
                    </button>
                  )}
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    title="Top up credits"
                    onClick={e => { e.stopPropagation(); setTopUpTarget(org) }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#34d399'; e.currentTarget.style.color = '#34d399' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                    💰
                  </button>
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                    style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                    title="View organisation"
                    onClick={e => { e.stopPropagation(); onSelectOrg(org) }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                    <svg viewBox="0 0 16 16" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/>
                    </svg>
                  </button>
                </div>
              </div>
            ))}
            </div>
          </div>
        </>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-5">
          <p className="text-xs" style={{ color: 'var(--text-3)' }}>
            Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchOrgs(p) }} disabled={page === 1}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>← Prev</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button key={n} onClick={() => { setPage(n); fetchOrgs(n) }}
                className="w-8 h-8 rounded-lg text-xs font-semibold transition-all"
                style={n === page
                  ? { background: 'var(--brand)', color: '#fff' }
                  : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                {n}
              </button>
            ))}
            <button onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchOrgs(p) }} disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-30"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Next →</button>
          </div>
        </div>
      )}
    </>
  )
}

// ── All Users Tab ─────────────────────────────────────────────────────────────

function AllUsersView({ showToast }) {
  const navigate = useNavigate()
  const [users,       setUsers]       = useState(null)
  const [total,       setTotal]       = useState(0)
  const [totalPages,  setTotalPages]  = useState(1)
  const [search,      setSearch]      = useState('')
  const [page,        setPage]        = useState(1)
  const [perPage,     setPerPage]     = useState(10)
  const [type,        setType]        = useState('all')
  const [approveUser, setApproveUser] = useState(null)
  const [removeUser,  setRemoveUser]  = useState(null)
  const [extendUser,  setExtendUser]  = useState(null)
  const [extendDays,  setExtendDays]  = useState('60')
  const [topUpTarget, setTopUpTarget] = useState(null)

  function fetchUsers(pg = page, limit = perPage, q = search, t = type) {
    setUsers(null)
    const params = new URLSearchParams({ page: pg, limit, type: t, ...(q ? { search: q } : {}) })
    api.get(`/api/users/all?${params}`)
      .then(data => { setUsers(data.users); setTotal(data.total); setTotalPages(data.totalPages) })
      .catch(() => { setUsers([]); setTotal(0); setTotalPages(1) })
  }

  useEffect(() => { fetchUsers() }, [])

  async function handleApprove() {
    const u = approveUser; setApproveUser(null)
    try {
      await api.patch(`/api/users/${u._id}/approve`)
      showToast(`${u.name} approved.`)
      fetchUsers()
    } catch { showToast('Failed to approve user.', 'error') }
  }

  async function handleRemove() {
    const u = removeUser; setRemoveUser(null)
    try {
      await api.delete(`/api/users/${u._id}`)
      showToast(`${u.name} deleted.`)
      fetchUsers()
    } catch { showToast('Failed to delete user.', 'error') }
  }

  async function handleExtend() {
    const u = extendUser; const days = parseInt(extendDays); setExtendUser(null)
    if (!days || days <= 0) return
    try {
      const updated = await api.patch(`/api/users/${u._id}/extend-expiry`, { days })
      setUsers(prev => prev.map(x => x._id === u._id ? { ...x, accountExpiresAt: updated.accountExpiresAt } : x))
      showToast(`${u.name}'s account extended by ${days} day${days !== 1 ? 's' : ''}.`)
    } catch { showToast('Failed to extend account.', 'error') }
  }

  function ValidityBadge({ expiresAt }) {
    if (!expiresAt) return <span className="text-xs" style={{ color: 'var(--text-3)' }}>—</span>
    const now = new Date(); const exp = new Date(expiresAt)
    const daysLeft = Math.ceil((exp - now) / 86400000)
    const color = daysLeft <= 0 ? '#f87171' : daysLeft <= 30 ? '#fbbf24' : '#34d399'
    return <span className="text-xs font-medium" style={{ color }}>{fmtDate(expiresAt)}</span>
  }

  return (
    <div>
      {/* Search + controls */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative w-full sm:w-56">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
            <input type="text" value={search}
              onChange={e => { const q = e.target.value; setSearch(q); setPage(1); fetchUsers(1, perPage, q, type) }}
              placeholder="Search users…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
              onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
            {search && (
              <button onClick={() => { setSearch(''); setPage(1); fetchUsers(1, perPage, '', type) }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>✕</button>
            )}
          </div>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>{total} user{total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2 sm:ml-auto">
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
            <select value={perPage} onChange={e => { const l = Number(e.target.value); setPerPage(l); setPage(1); fetchUsers(1, l, search, type) }}
              className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        {/* Filter pills */}
        <div className="flex items-center gap-2">
          {[
            { key: 'all',        label: 'All'          },
            { key: 'org',        label: '🏢 Organisation' },
            { key: 'standalone', label: '👤 Standalone'  },
          ].map(f => (
            <button key={f.key} onClick={() => { setType(f.key); setPage(1); fetchUsers(1, perPage, search, f.key) }}
              className="px-3 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                background: type === f.key ? 'var(--brand)' : 'var(--surface-2)',
                color:      type === f.key ? '#fff'         : 'var(--text-3)',
                border:     `1px solid ${type === f.key ? 'var(--brand)' : 'var(--border)'}`,
              }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Modals */}
      {approveUser && (
        <ConfirmModal title="Approve User"
          message={`Approve ${approveUser.name}? They will be able to log in.`}
          confirmLabel="Approve" success
          onConfirm={handleApprove} onCancel={() => setApproveUser(null)} />
      )}
      {removeUser && (
        <ConfirmModal title="Delete User"
          message={`Delete ${removeUser.name}? This cannot be undone.`}
          confirmLabel="Delete" danger
          onConfirm={handleRemove} onCancel={() => setRemoveUser(null)} />
      )}
      {extendUser && (() => {
        const days = parseInt(extendDays) || 0
        const now = new Date()
        const current = extendUser.accountExpiresAt ? new Date(extendUser.accountExpiresAt) : null
        const isValid = current && current > now
        const base = isValid ? current : now
        const newExp = days > 0 ? new Date(base.getTime() + days * 86400000) : null
        return createPortal(
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="card p-6 shadow-2xl animate-fade-up" style={{ minWidth: '22rem' }}>
              <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--brand)' }}>Extend Account</p>
              <p className="text-sm font-semibold mb-3" style={{ color: 'var(--text-1)' }}>{extendUser.name}</p>
              <div className="rounded-xl px-3 py-2.5 mb-4 text-xs space-y-1" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <div className="flex justify-between">
                  <span style={{ color: 'var(--text-3)' }}>Current expiry</span>
                  <span className="font-semibold" style={{ color: current ? (isValid ? '#34d399' : '#f87171') : 'var(--text-3)' }}>
                    {current ? fmtDate(current) : '—'}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-1" style={{ borderColor: 'var(--border)' }}>
                  <span style={{ color: 'var(--text-3)' }}>New expiry</span>
                  <span className="font-bold" style={{ color: newExp ? 'var(--brand)' : 'var(--text-3)' }}>
                    {newExp ? fmtDate(newExp) : '—'}
                  </span>
                </div>
              </div>
              <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-2)' }}>Days to extend</label>
              <input type="number" min="1" max="3650" value={extendDays} onChange={e => setExtendDays(e.target.value)}
                className="w-full px-3 py-2 rounded-xl text-sm outline-none mb-4"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
                autoFocus />
              <div className="flex gap-2">
                <button onClick={() => setExtendUser(null)} className="flex-1 py-2 rounded-xl text-sm font-semibold"
                  style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>Cancel</button>
                <button onClick={handleExtend} disabled={!newExp} className="flex-1 py-2 rounded-xl text-sm font-semibold disabled:opacity-40"
                  style={{ background: 'var(--brand)', color: '#fff' }}>Extend</button>
              </div>
            </div>
          </div>,
          document.body
        )
      })()}
      {topUpTarget && (
        <TopUpModal target={topUpTarget} showToast={showToast} onClose={() => setTopUpTarget(null)}
          onSaved={updated => {
            setUsers(prev => prev.map(u => u._id === updated._id ? { ...u, creditsBalance: updated.creditsBalance } : u))
            setTopUpTarget(null)
          }} />
      )}

      {!users ? (
        <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>
      ) : total === 0 && !search ? (
        <div className="card p-10 text-center">
          <p className="text-3xl mb-2">👤</p>
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-1)' }}>No users yet</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>Users appear here once they register.</p>
        </div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {users.map(u => {
              const isStandalone = !u.orgId
              const approved = u.status === 'approved'
              return (
                <div key={u._id} className="card p-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0"
                         style={{ background: 'var(--brand)', color: '#fff' }}>
                      {u.name?.[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {/* {isStandalone && !approved && (
                        <button onClick={() => setApproveUser(u)} title="Approve"
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}>
                          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 8.5l3.5 3.5 7-7"/></svg>
                        </button>
                      )} */}
                      {isStandalone && approved && (
                        <button onClick={() => { setExtendUser(u); setExtendDays('60') }} title="Extend"
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 1.5"/><path d="M11.5 1.5l2 2-2 2"/><path d="M13.5 3.5H11"/></svg>
                        </button>
                      )}
                      {isStandalone && (
                        <button onClick={() => setTopUpTarget(u)} title="Top up credits"
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                          💰
                        </button>
                      )}
                      {/* {isStandalone && approved && (
                        <button onClick={() => navigate('/history', { state: { userId: u._id, userName: u.name } })} title="View history"
                          className="w-7 h-7 rounded-lg flex items-center justify-center"
                          style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}>
                          <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                        </button>
                      )} */}
                      <button onClick={() => setRemoveUser(u)} title="Delete user"
                        className="w-7 h-7 rounded-lg flex items-center justify-center text-sm"
                        style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}>✗</button>
                    </div>
                  </div>
                  <div className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: 'var(--text-3)' }}>
                    <span>{u.orgName ? `🏢 ${u.orgName}` : '👤 Standalone'}</span>
                    {u.phone?.number && <span>{u.phone.countryCode} {u.phone.number}</span>}
                    <span>Joined: {fmtDate(u.createdAt)}</span>
                    {isStandalone && <ValidityBadge expiresAt={u.accountExpiresAt} />}
                    {isStandalone && (
                      <span className="flex items-center gap-1">Credits: <CreditsBadge balance={u.creditsBalance} /></span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block card overflow-x-auto">
            <div style={{ minWidth: '860px' }}>
            <div className="grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                 style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 0.8fr 1.2fr', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
              <span>User</span><span>Organisation</span><span>Mobile</span><span>Status</span><span>Valid Till</span><span>Credits</span><span className="text-center">Actions</span>
            </div>
            {users.length === 0 ? (
              <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-3)' }}>No users match your search.</div>
            ) : users.map(u => {
              const isStandalone = !u.orgId
              const approved = u.status === 'approved'
              return (
                <div key={u._id} className="grid px-6 py-4 items-center border-b last:border-0 transition-colors"
                     style={{ gridTemplateColumns: '2fr 1.5fr 1fr 1fr 1fr 0.8fr 1.2fr', borderColor: 'var(--border)' }}
                     onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                     onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                         style={{ background: 'var(--brand)', color: '#fff' }}>
                      {u.name?.[0]?.toUpperCase() ?? '?'}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{u.name}</p>
                      <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{u.email}</p>
                    </div>
                  </div>
                  <div className="text-sm min-w-0">
                    {u.orgName
                      ? <span className="truncate block" style={{ color: 'var(--text-2)' }}>{u.orgName}</span>
                      : <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'var(--surface-2)', color: 'var(--text-3)', border: '1px solid var(--border)' }}>Standalone</span>}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-3)' }}>
                    {u.phone?.number ? `${u.phone.countryCode} ${u.phone.number}` : '—'}
                  </div>
                  <div>
                    <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                      style={approved
                        ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                        : { background: 'rgba(251,191,36,0.12)', color: '#fbbf24' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: approved ? '#34d399' : '#fbbf24' }} />
                      {approved ? 'Active' : 'Pending'}
                    </span>
                  </div>
                  <div><ValidityBadge expiresAt={u.accountExpiresAt} /></div>
                  <div><CreditsBadge balance={isStandalone ? u.creditsBalance : null} /></div>
                  <div className="flex items-center justify-center gap-1.5">
                    {/* {isStandalone && !approved && (
                      <button onClick={() => setApproveUser(u)} title="Approve user"
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                        style={{ color: '#34d399', border: '1px solid rgba(52,211,153,0.3)', background: 'rgba(52,211,153,0.08)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(52,211,153,0.18)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'rgba(52,211,153,0.08)'}>
                        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 8.5l3.5 3.5 7-7"/></svg>
                      </button>
                    )} */}
                    {isStandalone && approved && (
                      <button onClick={() => { setExtendUser(u); setExtendDays('60') }} title="Extend account validity"
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                        style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#fbbf24'; e.currentTarget.style.color = '#fbbf24' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="8" r="6"/><path d="M8 5v3l2 1.5"/><path d="M11.5 1.5l2 2-2 2"/><path d="M13.5 3.5H11"/></svg>
                      </button>
                    )}
                    {/* {isStandalone && approved && (
                      <button onClick={() => navigate('/history', { state: { userId: u._id, userName: u.name } })} title="View exam history"
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                        style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand)'; e.currentTarget.style.color = 'var(--brand)' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z"/><circle cx="8" cy="8" r="2"/></svg>
                      </button>
                    )} */}
                    {isStandalone && (
                      <button onClick={() => setTopUpTarget(u)} title="Top up credits"
                        className="w-7 h-7 rounded-lg flex items-center justify-center transition"
                        style={{ color: 'var(--text-3)', border: '1px solid var(--border)' }}
                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#34d399'; e.currentTarget.style.color = '#34d399' }}
                        onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-3)' }}>
                        💰
                      </button>
                    )}
                    <button onClick={() => setRemoveUser(u)} title="Delete user"
                      className="w-7 h-7 rounded-lg flex items-center justify-center transition text-sm"
                      style={{ color: '#f87171', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.08)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.18)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'rgba(248,113,113,0.08)'}>✗</button>
                  </div>
                </div>
              )
            })}
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchUsers(p, perPage, search, type) }}
                disabled={page === 1} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>← Prev</button>
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>Page {page} of {totalPages}</span>
              <button onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchUsers(p, perPage, search, type) }}
                disabled={page === totalPages} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Exams Tab ─────────────────────────────────────────────────────────────────

function AllExamsView({ showToast }) {
  const [allExams, setAllExams] = useState(null)
  const [selectedExam, setSelectedExam] = useState(null)
  const [creating, setCreating] = useState(false)
  const [search,   setSearch]   = useState('')
  const [confirmDel, setConfirmDel] = useState(null)
  const [saving, setSaving] = useState(false)

  const normalizeExam = e => ({ ...e, id: e._id?.toString() ?? e.id })

  useEffect(() => {
    api.get('/api/exams?limit=200')
      .then(data => setAllExams((data.exams ?? data).map(normalizeExam)))
      .catch(() => setAllExams([]))
  }, [])

  // Poll every 10s while any exam is still generating
  useEffect(() => {
    const hasGenerating = (allExams ?? []).some(e => e.questionStatus === 'generating')
    if (!hasGenerating) return
    const id = setInterval(() => {
      api.get('/api/exams?limit=200')
        .then(data => setAllExams((data.exams ?? data).map(normalizeExam)))
        .catch(() => {})
    }, 10000)
    return () => clearInterval(id)
  }, [allExams])

  const filtered = (allExams ?? []).filter(e =>
    !search || e.name.toLowerCase().includes(search.toLowerCase()) || (e.description ?? '').toLowerCase().includes(search.toLowerCase())
  )

  async function handleDelete(exam) {
    try {
      await api.delete(`/api/exams/${exam._id}`)
      setAllExams(prev => prev.filter(e => e._id !== exam._id))
      setConfirmDel(null)
      showToast('Exam deleted.')
    } catch (err) {
      showToast(err.message, 'error')
      setConfirmDel(null)
    }
  }

  async function handleCreate({ name, description, textbookFiles, patternFiles, questionSettings, difficulty, bloomEnabled, bloomDistribution, bloomMarks, practiceMultiplier, trialsEnabled, trialLevelLockEnabled, showQuestionTypeIndicator, validFrom, validTill }) {
    setSaving(true)
    let examId = null
    try {
      const raw  = await api.post('/api/exams', { name, description, questionSettings, difficulty, bloomEnabled, bloomDistribution, bloomMarks, practiceMultiplier, trialsEnabled, trialLevelLockEnabled, showQuestionTypeIndicator, validFrom: validFrom ?? null, validTill: validTill ?? null })
      const exam = { ...raw, id: raw._id?.toString() ?? raw.id }
      examId = exam.id

      for (const file of textbookFiles) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'textbookSources', size: file.size,
        })
        exam.textbookSources = [...(exam.textbookSources ?? []), source]
      }

      for (const file of patternFiles) {
        const { uploadUrl, filePath } = await api.post('/api/upload/signed-url', {
          type: 'examPdf', examId: exam.id, filename: file.name, contentType: 'application/pdf',
        })
        await api.putFile(uploadUrl, file, null, { contentType: 'application/pdf' })
        const { source } = await api.post(`/api/exams/${exam.id}/confirm-pdf`, {
          filePath, name: file.name, sourceKey: 'patternSources', size: file.size,
        })
        exam.patternSources = [...(exam.patternSources ?? []), source]
      }

      if (textbookFiles.length) {
        await api.post(`/api/exams/${exam.id}/generate`)
        exam.questionStatus = 'generating'
      }

      setAllExams(prev => [exam, ...(prev ?? [])])
      setCreating(false)
      showToast("Exam created — questions are being generated. We'll notify you via email once ready.", 'info')
    } catch (err) {
      // Creation partially failed after the exam record was already inserted (e.g. a PDF
      // upload dropped mid-flight) — remove it rather than leaving a stuck, PDF-less orphan
      // with questionStatus permanently 'pending'.
      if (examId) {
        try { await api.delete(`/api/exams/${examId}`) } catch { /* best-effort cleanup */ }
      }
      showToast(err.message || 'Failed to create exam. Please try again.', 'error')
    } finally {
      setSaving(false)
    }
  }

  function cancelCreate() { setCreating(false) }

  function handleExamUpdate(updated) {
    if (updated._deleted) {
      setAllExams(prev => prev.filter(e => e.id !== updated.id))
    } else {
      setAllExams(prev => prev.map(e => e.id === updated.id ? { ...e, ...updated } : e))
      setSelectedExam(prev => prev ? { ...prev, ...updated } : null)
    }
  }

  if (selectedExam) {
    return (
      <ExamDetail
        exam={selectedExam}
        onBack={() => setSelectedExam(null)}
        onExamUpdate={handleExamUpdate}
        showToast={showToast}
      />
    )
  }

  return (
    <>
      {confirmDel && (
        <ConfirmModal
          title="Delete Exam"
          message={`Delete "${confirmDel.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          danger
          onConfirm={() => handleDelete(confirmDel)}
          onCancel={() => setConfirmDel(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5">
        <p className="text-xs text-[var(--text-3)]">Exams created here are visible to all standalone users.</p>
        {!creating && (
          <button onClick={() => setCreating(true)} className="btn-primary text-sm">+ New Exam</button>
        )}
      </div>

      {creating && (
        <CreateExamForm onSubmit={handleCreate} onCancel={cancelCreate} saving={saving} />
      )}

      {allExams === null ? (
        <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
            <div className="relative w-full sm:w-56">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Search exams…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
                onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
                onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
            </div>
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>{filtered.length} exam{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          {filtered.length === 0 ? (
            <div className="card p-10 text-center">
              <p className="text-3xl mb-2">📚</p>
              <p className="text-sm" style={{ color: 'var(--text-3)' }}>No exams found.</p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="hidden sm:grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                   style={{ gridTemplateColumns: '2fr 1.5fr 0.8fr 80px 80px', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
                <span>Exam</span><span>Validity</span><span>Created</span><span>Status</span><span className="text-center">Actions</span>
              </div>
              {filtered.map(exam => {
                const validity = exam.validFrom || exam.validTill
                  ? `${exam.validFrom ? fmtDate(exam.validFrom) : '—'} – ${exam.validTill ? fmtDate(exam.validTill) : '—'}`
                  : '—'
                const statusBadge = exam.questionStatus && exam.questionStatus !== 'ready'
                  ? <QuestionStatusBadge status={exam.questionStatus} />
                  : (
                    <span className="inline-flex items-center gap-1 text-[0.65rem] font-semibold px-2 py-0.5 rounded-full"
                      style={exam.enabled !== false
                        ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                        : { background: 'rgba(248,113,113,0.1)', color: '#f87171' }}>
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: exam.enabled !== false ? '#34d399' : '#f87171' }} />
                      {exam.enabled !== false ? 'Active' : 'Inactive'}
                    </span>
                  )
                return (
                  <div key={exam.id}>
                    {/* Desktop row */}
                    <div className="hidden sm:grid px-6 py-4 items-center border-b last:border-0 transition-colors cursor-pointer"
                         style={{ gridTemplateColumns: '2fr 1.5fr 0.8fr 80px 80px', borderColor: 'var(--border)' }}
                         onClick={() => setSelectedExam({ ...exam, id: exam.id ?? exam._id?.toString() })}
                         onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                         onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-xs flex-shrink-0"
                             style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)' }}>📝</div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{exam.name}</p>
                          {exam.description && <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-3)' }}>{exam.description}</p>}
                        </div>
                      </div>
                      <p className="text-xs" style={{ color: 'var(--text-3)' }}>{validity}</p>
                      <p className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDate(exam.createdAt)}</p>
                      {statusBadge}
                      <div className="flex justify-center" onClick={e => e.stopPropagation()}>
                        <button onClick={e => { e.stopPropagation(); setConfirmDel(exam) }}
                          className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-sm">✗</button>
                      </div>
                    </div>

                    {/* Mobile row */}
                    <div className="sm:hidden px-4 py-4 border-b last:border-0 cursor-pointer transition-colors"
                         style={{ borderColor: 'var(--border)' }}
                         onClick={() => setSelectedExam({ ...exam, id: exam.id ?? exam._id?.toString() })}
                         onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                         onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-sm flex-shrink-0"
                             style={{ background: 'rgba(14,165,233,0.1)', border: '1px solid rgba(14,165,233,0.2)' }}>📝</div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{exam.name}</p>
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                              {validity !== '—' ? `${validity} · ` : ''}{fmtDate(exam.createdAt)}
                            </p>
                            {statusBadge}
                          </div>
                        </div>
                        <div onClick={e => e.stopPropagation()}>
                          <button onClick={e => { e.stopPropagation(); setConfirmDel(exam) }}
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-red-400 hover:bg-red-500/10 transition-all text-sm">✗</button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}
    </>
  )
}

// ── Token Usage Tab ───────────────────────────────────────────────────────────

const SKILLASSESS_ENDPOINT_LABELS = {
  'questions-generate':        'Generate Questions',
  'check-short-answer':        'Check Answer (Trial)',
  'check-short-answers-batch': 'Check Answers',
}

// From adminportal's own aiUsage collection (SoilPulse/RadtarBot/WhatsApp) —
// merged in by the backend, not native to this service's own AiUsageLog.
// Grouped under one "Others" top-level pill instead of sitting flat alongside
// SkillAssess's own three, since they're a different system's calls — the
// sub-pill row below only appears once "Others" (or one of these) is active.
const ADMINPORTAL_ENDPOINT_LABELS = {
  'soilpulse_recommendation':  'SoilPulse Recommendation',
  'soilpulse_translation':     'SoilPulse Translation',
  'radtarbot_chat':            'RadtarBot Chat',
  'whatsapp_bot':              'WhatsApp Bot',
}

const ADMINPORTAL_ENDPOINT_KEYS = Object.keys(ADMINPORTAL_ENDPOINT_LABELS)

const ENDPOINT_LABELS = { ...SKILLASSESS_ENDPOINT_LABELS, ...ADMINPORTAL_ENDPOINT_LABELS }

// True once the user has drilled into "Others" (combined or one specific
// adminportal source) — used to show the sub-pill row and drop the Exam
// column, which is always empty for these rows (adminportal has no exams).
function isOthersView(endpoint) {
  return endpoint === 'others' || ADMINPORTAL_ENDPOINT_KEYS.includes(endpoint)
}

// Table grid columns — order is Endpoint, Used By, then Exam (SkillAssess's
// own rows) or Mobile No (Others view, adminportal rows have no exam concept
// but do carry a mobile number), so the layout stays one consistent 7 columns.
const GRID_COLUMNS = '1.3fr 1.6fr 1.4fr 0.8fr 0.8fr 0.8fr 1fr 1.2fr'

function fmtTokens(n) {
  if (n == null) return '—'
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`
  return String(n)
}

function fmtCost(n) {
  if (n == null) return '—'
  if (n === 0) return '₹0.00'
  if (n < 0.01) return `₹${n.toFixed(4)}`
  return `₹${n.toFixed(2)}`
}

function fmtDateTime(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Single "Used By" identity per call: the student for check-answer calls (a live
// user request), or the org for questions-generate (a Cloud-Tasks background job
// with no end-user — attributed to whichever org's exam triggered it).
// Adminportal rows (tagged with `environment`) have no org concept at all, so
// the User/Org badge is meaningless there and the empty case reads "NA"
// instead of "Unknown" — this is a resolvable identity that just isn't known
// yet (no user record for that mobile), not an unmodeled kind of caller.
function UsedByCell({ log }) {
  const isAdminPortalRow = !!log.environment
  const isUser = !!log.userName
  const name = isUser ? log.userName : (isAdminPortalRow ? 'NA' : (log.orgName ?? 'Unknown'))
  return (
    <span className="flex items-center max-w-full min-w-0 gap-2">
      <span className="truncate min-w-0" title={name}>{name}</span>
      {!isAdminPortalRow && (
        <span className="text-[0.55rem] font-semibold px-1 py-px rounded-full flex-shrink-0 whitespace-nowrap leading-none"
              style={{ background: 'var(--surface-2)', color: 'var(--text-3)', border: '1px solid var(--border)' }}>
          {isUser ? 'User' : 'Org'}
        </span>
      )}
    </span>
  )
}

function StatTile({ label, value, sub }) {
  return (
    <div className="card p-4">
      <p className="text-xs font-semibold uppercase tracking-widest mb-1.5" style={{ color: 'var(--text-3)' }}>{label}</p>
      <p className="text-2xl font-bold" style={{ color: 'var(--text-1)' }}>{value}</p>
      {sub && <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{sub}</p>}
    </div>
  )
}

function AiUsageView() {
  const [summary,    setSummary]    = useState(null)
  const [logs,       setLogs]       = useState(null)
  const [total,      setTotal]      = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [search,     setSearch]     = useState('')
  const [page,       setPage]       = useState(1)
  const [perPage,    setPerPage]    = useState(25)
  const [endpoint,   setEndpoint]   = useState('all')
  const [fromDate,   setFromDate]   = useState('')
  const [toDate,     setToDate]     = useState('')
  const [orgId,      setOrgId]      = useState('')
  const [orgs,       setOrgs]       = useState([])
  // Only meaningful for the four adminportal-sourced tags (SoilPulse/RadtarBot/
  // WhatsApp) — 'all' shows both prod+dev adminportal rows together (each still
  // tagged per-row so they stay visually distinguishable), never silently merged
  // into one indistinguishable number. Has no effect on SkillAssess's own rows.
  const [adminEnv,   setAdminEnv]   = useState('all')
  // This whole table/summary is raw backend AI spend, deliberately margin/GST-free
  // (see aiPricing.js) — but reading off "what would this actually cost a customer"
  // is a common question here, so the live-configured rates are fetched once to
  // compute an informational column alongside the raw cost, not to replace it.
  const [rates, setRates] = useState({ marginPct: 100, gstPct: 18 })

  useEffect(() => { api.get('/api/orgs?all=true').then(setOrgs).catch(() => setOrgs([])) }, [])
  useEffect(() => {
    api.get('/api/platform-settings')
      .then(s => setRates({ marginPct: s.marginPct ?? 100, gstPct: s.gstPct ?? 18 }))
      .catch(() => {})
  }, [])

  function billedCost(rawCost) {
    if (rawCost == null) return null
    return rawCost * (1 + rates.marginPct / 100) * (1 + rates.gstPct / 100)
  }

  function fetchLogs(pg = page, limit = perPage, q = search, ep = endpoint, from = fromDate, to = toDate, org = orgId, aEnv = adminEnv) {
    setLogs(null)
    const params = new URLSearchParams({
      page: pg, limit, endpoint: ep, adminEnv: aEnv,
      ...(q   ? { search: q } : {}),
      ...(org ? { orgId: org } : {}),
      ...(from ? { from: new Date(from).toISOString() } : {}),
      ...(to   ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
    })
    api.get(`/api/ai-usage?${params}`)
      .then(data => { setLogs(data.logs); setTotal(data.total); setTotalPages(data.totalPages) })
      .catch(() => { setLogs([]); setTotal(0); setTotalPages(1) })
  }

  function fetchSummary(q = search, ep = endpoint, from = fromDate, to = toDate, org = orgId, aEnv = adminEnv) {
    const params = new URLSearchParams({
      endpoint: ep, adminEnv: aEnv,
      ...(q   ? { search: q } : {}),
      ...(org ? { orgId: org } : {}),
      ...(from ? { from: new Date(from).toISOString() } : {}),
      ...(to   ? { to: new Date(`${to}T23:59:59.999`).toISOString() } : {}),
    })
    api.get(`/api/ai-usage/summary?${params}`).then(setSummary).catch(() => setSummary(null))
  }

  // Every filter change re-fetches both the table and the summary stats/top-orgs
  // above it, so the cards always reflect the currently applied filters.
  function applyFilters({ q = search, ep = endpoint, from = fromDate, to = toDate, org = orgId, aEnv = adminEnv } = {}) {
    // Org doesn't apply to adminportal rows — and worse, the backend treats
    // any active org filter as "exclude adminportal rows entirely" (they have
    // no orgId to match), so a stale selection carried over from a SkillAssess
    // tag would silently show nothing at all once the user switches to Others.
    if (isOthersView(ep)) org = ''
    setSearch(q); setEndpoint(ep); setFromDate(from); setToDate(to); setOrgId(org); setAdminEnv(aEnv); setPage(1)
    fetchLogs(1, perPage, q, ep, from, to, org, aEnv)
    fetchSummary(q, ep, from, to, org, aEnv)
  }

  useEffect(() => { fetchLogs(); fetchSummary() }, [])

  return (
    <div>
      {/* Stat tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-2">
        <StatTile label="Total Calls" value={summary ? summary.totals.calls.toLocaleString() : '—'} />
        <StatTile label="Input Tokens" value={summary ? fmtTokens(summary.totals.inputTokens) : '—'} />
        <StatTile label="Output Tokens" value={summary ? fmtTokens(summary.totals.outputTokens) : '—'} />
        <StatTile label="Total Tokens" value={summary ? fmtTokens(summary.totals.inputTokens + summary.totals.outputTokens) : '—'} />
        <StatTile label="Cost" value={summary ? fmtCost(summary.totals.cost) : '—'} />
      </div>
      {summary?.unpricedCalls > 0 && (
        <p className="text-xs mb-4" style={{ color: 'var(--text-3)' }}>
          ⚠ {summary.unpricedCalls} call{summary.unpricedCalls !== 1 ? 's' : ''} used a model with no confirmed rate — cost for those is shown as ₹0, so the total may be an undercount.
        </p>
      )}
      {!summary?.unpricedCalls && <div className="mb-6" />}

      {/* Top orgs by usage */}
      {summary?.byOrg?.length > 0 && (
        <div className="card p-4 mb-6">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'var(--text-3)' }}>Top Orgs by Cost</p>
          <div className="space-y-2">
            {summary.byOrg.map(o => {
              const maxCost = summary.byOrg[0].cost
              const pct = maxCost > 0 ? (o.cost / maxCost) * 100 : 0
              return (
                <div key={o.orgId ?? 'unknown'} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span className="text-xs w-24 sm:w-44 truncate flex-shrink-0" style={{ color: 'var(--text-2)' }} title={o.orgName}>{o.orgName}</span>
                  <div className="flex-1 min-w-[4rem] h-2 rounded-full overflow-hidden" style={{ background: 'var(--surface-2)' }}>
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: 'var(--brand)' }} />
                  </div>
                  <span className="text-xs w-full sm:w-28 sm:text-right flex-shrink-0 tabular-nums" style={{ color: 'var(--text-3)' }}>
                    {fmtCost(o.cost)} · {fmtTokens(o.inputTokens + o.outputTokens)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Search + controls */}
      <div className="mb-4 flex flex-col gap-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative w-full sm:w-56">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
            <input type="text" value={search}
              onChange={e => applyFilters({ q: e.target.value })}
              placeholder="Search exam, org, or user…"
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
              onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'} />
          </div>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>{total} call{total !== 1 ? 's' : ''}</span>
          <div className="flex items-center gap-2 sm:ml-auto">
            <span className="text-xs" style={{ color: 'var(--text-3)' }}>Rows per page</span>
            <select value={perPage} onChange={e => { const l = Number(e.target.value); setPerPage(l); setPage(1); fetchLogs(1, l, search, endpoint) }}
              className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        {/* Endpoint filter pills — SkillAssess's own three plus one "Others" pill
            standing in for all four adminportal sources combined. */}
        <div className="flex items-center gap-2 flex-wrap">
          {[
            { key: 'all', label: 'All' },
            ...Object.entries(SKILLASSESS_ENDPOINT_LABELS).map(([key, label]) => ({ key, label })),
            { key: 'others', label: 'Others' },
          ].map(f => {
            const active = f.key === 'others' ? isOthersView(endpoint) : endpoint === f.key
            return (
              <button key={f.key} onClick={() => applyFilters({ ep: f.key })}
                className="px-3 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  background: active ? 'var(--brand)' : 'var(--surface-2)',
                  color:      active ? '#fff'         : 'var(--text-3)',
                  border:     `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                }}>
                {f.label}
              </button>
            )
          })}
        </div>

        {/* Others sub-pills — which specific adminportal source, once "Others" is active */}
        {isOthersView(endpoint) && (
          <div className="flex items-center gap-2 flex-wrap pl-3" style={{ borderLeft: '2px solid var(--border)' }}>
            {[{ key: 'others', label: 'All Others' }, ...Object.entries(ADMINPORTAL_ENDPOINT_LABELS).map(([key, label]) => ({ key, label }))].map(f => (
              <button key={f.key} onClick={() => applyFilters({ ep: f.key })}
                className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
                style={{
                  background: endpoint === f.key ? 'var(--surface-2)' : 'transparent',
                  color:      endpoint === f.key ? 'var(--text-1)'    : 'var(--text-3)',
                  border:     `1px solid ${endpoint === f.key ? 'var(--border)' : 'transparent'}`,
                }}>
                {f.label}
              </button>
            ))}
          </div>
        )}

        {/* Org + date range filter */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Org has no meaning for adminportal rows (no org concept) — hidden
              in the Others view rather than shown as a no-op control. */}
          {!isOthersView(endpoint) && (
            <>
              <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>Org</span>
              <select value={orgId} onChange={e => applyFilters({ org: e.target.value })}
                title={orgs.find(o => o._id === orgId)?.name ?? 'All Organisations'}
                className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer truncate"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)', maxWidth: '11rem' }}>
                <option value="">All Organisations</option>
                {orgs.map(o => <option key={o._id} value={o._id}>{o.name}</option>)}
              </select>
            </>
          )}
          {/* Only affects the four adminportal tags (SoilPulse/RadtarBot/WhatsApp) —
              SkillAssess's own rows aren't environment-scoped and ignore this. */}
          <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }} title="Only applies to SoilPulse/RadtarBot/WhatsApp rows">Env</span>
          <select value={adminEnv} onChange={e => applyFilters({ aEnv: e.target.value })}
            title="Only applies to SoilPulse/RadtarBot/WhatsApp rows"
            className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}>
            <option value="all">All</option>
            <option value="prod">Prod</option>
            <option value="dev">Dev</option>
          </select>
          <span className="text-xs flex-shrink-0" style={{ color: 'var(--text-3)' }}>From</span>
          <input type="date" value={fromDate} max={toDate || undefined}
            onChange={e => applyFilters({ from: e.target.value })}
            className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>To</span>
          <input type="date" value={toDate} min={fromDate || undefined}
            onChange={e => applyFilters({ to: e.target.value })}
            className="text-xs rounded-lg px-2 py-1.5 outline-none cursor-pointer"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          {(fromDate || toDate || orgId) && (
            <button onClick={() => applyFilters({ from: '', to: '', org: '' })}
              className="text-xs font-medium px-2 py-1 rounded-lg" style={{ color: 'var(--text-3)' }}>
              ✕ Clear org/dates
            </button>
          )}
        </div>
      </div>

      {!logs ? (
        <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>
      ) : total === 0 && !search ? (
        <div className="card p-10 text-center">
          <p className="text-3xl mb-2">📊</p>
          <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-1)' }}>No AI usage yet</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>Calls appear here as exams are generated and answers graded.</p>
        </div>
      ) : (
        <>
          {/* Table — horizontally scrollable so the header row always stays visible,
              instead of switching to a headerless card layout on small screens */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <div style={{ minWidth: '52rem' }}>
                <div className="grid px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
                     style={{ gridTemplateColumns: GRID_COLUMNS, columnGap: '1rem', color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)' }}>
                  <span>Endpoint</span><span>Used By</span><span>{isOthersView(endpoint) ? 'Mobile No' : 'Exam'}</span><span>Input</span><span>Output</span><span>Cost</span><span title="Raw cost with the currently configured margin and GST applied — what this would actually be billed as, not what was deducted (this traffic may predate the current rates).">Billed Equiv.</span><span>Date</span>
                </div>
                {logs.length === 0 ? (
                  <div className="px-6 py-10 text-center text-sm" style={{ color: 'var(--text-3)' }}>No calls match your search.</div>
                ) : logs.map(l => (
                  <div key={l._id} className="grid px-6 py-4 items-center border-b last:border-0 transition-colors"
                       style={{ gridTemplateColumns: GRID_COLUMNS, columnGap: '1rem', borderColor: 'var(--border)' }}
                       onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-2)'}
                       onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <div className="text-xs font-medium flex items-center gap-1.5 min-w-0" style={{ color: 'var(--text-2)' }}>
                      <span className="truncate">{ENDPOINT_LABELS[l.endpoint] ?? l.endpoint}</span>
                      {l.environment && (
                        <span className="text-[0.55rem] font-semibold px-1 py-px rounded-full flex-shrink-0 whitespace-nowrap leading-none uppercase"
                              style={{
                                background: l.environment === 'prod' ? 'var(--warning-bg, #fef3c7)' : 'var(--surface-2)',
                                color:      l.environment === 'prod' ? 'var(--warning-text, #92400e)' : 'var(--text-3)',
                                border:     '1px solid var(--border)',
                              }}>
                          {l.environment}
                        </span>
                      )}
                    </div>
                    <div className="text-xs min-w-0" style={{ color: 'var(--text-2)' }}><UsedByCell log={l} /></div>
                    {isOthersView(endpoint) ? (
                      <div className="text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>{l.mobileNo ?? 'NA'}</div>
                    ) : (
                      <div className="text-sm truncate min-w-0" style={{ color: 'var(--text-1)' }} title={l.examName ?? undefined}>{l.examName ?? '—'}</div>
                    )}
                    <div className="text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>{fmtTokens(l.inputTokens)}</div>
                    <div className="text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>{fmtTokens(l.outputTokens)}</div>
                    <div className="text-xs tabular-nums" style={{ color: 'var(--text-2)' }}>{fmtCost(l.cost)}</div>
                    <div className="text-xs tabular-nums" style={{ color: 'var(--text-3)' }}>
                      {/* Margin/GST only apply to SkillAssess's own credits model — not
                          meaningful for SoilPulse/RadtarBot/WhatsApp traffic sharing this view. */}
                      {isOthersView(endpoint) ? '—' : fmtCost(billedCost(l.cost))}
                    </div>
                    <div className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDateTime(l.createdAt)}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              <button onClick={() => { const p = Math.max(1, page - 1); setPage(p); fetchLogs(p, perPage, search, endpoint) }}
                disabled={page === 1} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>← Prev</button>
              <span className="text-xs" style={{ color: 'var(--text-3)' }}>Page {page} of {totalPages}</span>
              <button onClick={() => { const p = Math.min(totalPages, page + 1); setPage(p); fetchLogs(p, perPage, search, endpoint) }}
                disabled={page === totalPages} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
                style={{ background: 'var(--surface-2)', color: 'var(--text-2)' }}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// ── Settings Tab ──────────────────────────────────────────────────────────────

function PlatformSettingsView({ showToast }) {
  const [settings, setSettings] = useState(null)
  const [saving,   setSaving]   = useState(false)

  useEffect(() => {
    api.get('/api/platform-settings')
      .then(setSettings)
      .catch(() => showToast('Failed to load platform settings.', 'error'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function update(key, value) {
    setSettings(prev => ({ ...prev, [key]: value }))
  }

  function updateFeaturePrice(key, value) {
    setSettings(prev => ({ ...prev, featureUnlockPrices: { ...prev.featureUnlockPrices, [key]: value } }))
  }

  async function handleSave() {
    setSaving(true)
    try {
      const saved = await api.patch('/api/platform-settings', {
        marginPct: Number(settings.marginPct),
        gstPct: Number(settings.gstPct),
        lowCreditsThresholdPct: Number(settings.lowCreditsThresholdPct),
        estimatedCostPerMB: Number(settings.estimatedCostPerMB),
        estimatedCostPerShortAnswerCheck: Number(settings.estimatedCostPerShortAnswerCheck),
        estimatedCostPerShortAnswerBatch: Number(settings.estimatedCostPerShortAnswerBatch),
        featureUnlockPrices: Object.fromEntries(
          FEATURE_PRICE_FIELDS.map(({ key }) => [key, Number(settings.featureUnlockPrices?.[key] ?? 0)])
        ),
      })
      setSettings(saved)
      showToast('Platform settings saved.')
    } catch (err) {
      showToast(err.message || 'Failed to save settings.', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (!settings) return <div className="card p-6 text-sm text-[var(--text-3)]">Loading…</div>

  const fields = [
    { key: 'marginPct', label: 'Margin on AI usage (%)' },
    { key: 'gstPct', label: 'GST on AI usage (%)' },
    { key: 'lowCreditsThresholdPct', label: 'Low-credits email threshold (%)', hint: 'A one-time "running low" email sends once a balance drops to this percentage of the account\'s most recent Razorpay purchase or below. No effect until an account has bought credits at least once.' },
    { key: 'estimatedCostPerMB', label: 'Estimated cost per MB of uploaded PDF (₹)', hint: 'Used to warn an organisation before generation starts if their balance clearly can’t cover the estimated cost of their uploaded PDFs.', step: '0.01' },
    { key: 'estimatedCostPerShortAnswerCheck', label: 'Estimated cost per short-answer check (₹)', hint: 'Used to block a single short-answer AI check (e.g. during Trials) before it runs if the balance can\'t cover it.', step: '0.01' },
    { key: 'estimatedCostPerShortAnswerBatch', label: 'Estimated cost per short-answer batch check (₹)', hint: 'Same as above, but for the batch check that grades all short-answer questions in one exam submission at once.', step: '0.01' },
  ]

  return (
    <div className="space-y-5">
      <div className="card p-6 space-y-5">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Credits</p>
        </div>
        {fields.map(f => (
          <div key={f.key}>
            <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-2)' }}>{f.label}</label>
            <input type="number" min="0" step={f.step ?? '1'} value={settings[f.key]} onChange={e => update(f.key, e.target.value)}
              className="w-full px-3 py-2 rounded-xl text-sm outline-none"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
            <p className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{f.hint}</p>
          </div>
        ))}
      </div>

      <div className="card p-6 space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Question Type &amp; Bloom's Taxonomy Pricing</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>
            The credits balance an organisation needs to unlock each one. 0 means always unlocked for everyone.
          </p>
        </div>
        {FEATURE_PRICE_FIELDS.map(f => (
          <div key={f.key} className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
            <label className="text-sm font-medium sm:w-40 sm:flex-shrink-0" style={{ color: 'var(--text-2)' }}>{f.label}</label>
            <input type="number" min="0" step="1" value={settings.featureUnlockPrices?.[f.key] ?? 0}
              onChange={e => updateFeaturePrice(f.key, e.target.value)}
              className="w-full sm:flex-1 sm:min-w-0 px-3 py-2 rounded-xl text-sm outline-none"
              style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          </div>
        ))}
      </div>

      <button onClick={handleSave} disabled={saving} className="btn-primary text-sm disabled:opacity-40">
        {saving ? 'Saving…' : 'Save Settings'}
      </button>
    </div>
  )
}

const FEATURE_PRICE_FIELDS = [
  { key: 'mcq',          label: 'MCQ' },
  { key: 'true_false',   label: 'True / False' },
  { key: 'msq',          label: 'MSQ' },
  { key: 'short_answer', label: 'Short Answer' },
  { key: 'bloomsTaxonomy', label: "Bloom's Taxonomy" },
]

// ── Credit Packages panel (Settings tab) ─────────────────────────────────────

// appearance-none + explicit height + custom chevron keeps this pixel-matched
// across browsers instead of relying on each browser's native <select> sizing.
function AudienceSelect({ value, onChange, size = 'sm', full = false }) {
  const isLg = size === 'lg'
  return (
    <div className={`relative flex-shrink-0 ${full ? 'w-full' : ''}`}>
      <select value={value} onChange={onChange}
        className={`${isLg ? 'h-10 pl-3 pr-8 text-sm' : 'h-7 pl-2 pr-6 text-xs'} rounded-lg outline-none cursor-pointer appearance-none ${full ? 'w-full' : ''}`}
        style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
        <option value="both">Both</option>
        <option value="organisation">Orgs only</option>
        <option value="standalone">Standalone only</option>
      </select>
      <svg className={`absolute ${isLg ? 'right-3' : 'right-1.5'} top-1/2 -translate-y-1/2 pointer-events-none`} width={isLg ? 12 : 10} height={isLg ? 12 : 10} viewBox="0 0 12 12" fill="none">
        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="var(--text-3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  )
}

function CreditPackagesPanel({ showToast }) {
  const [packages, setPackages] = useState(null)
  const [adding,   setAdding]   = useState(false)
  // const [newLabel, setNewLabel] = useState('') — label no longer shown/edited
  // anywhere (buyer-facing card just shows price); auto-derived from price
  // instead so the schema's required `label` field still gets a real value.
  const [newPrice,    setNewPrice]    = useState('')
  const [newAudience, setNewAudience] = useState('both')
  const [saving,     setSaving]     = useState(false)
  const [editingId,  setEditingId]  = useState(null)
  // const [editLabel, setEditLabel] = useState('') — see newLabel above.
  const [editPrice,   setEditPrice]   = useState('')
  const [editSaving,  setEditSaving]  = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting,     setDeleting]     = useState(false)

  function fetchPackages() {
    api.get('/api/credit-packages?includeInactive=true')
      .then(setPackages)
      .catch(() => { showToast('Failed to load credit packages.', 'error'); setPackages([]) })
  }

  useEffect(() => { fetchPackages() }, [])

  async function handleAdd() {
    const priceInr = Number(newPrice)
    if (!priceInr) return
    setSaving(true)
    try {
      // creditsAmount always equals priceInr — margin/GST only apply at
      // consumption (deductCredits), a purchase is always 1 credit per ₹1.
      // label is schema-required but no longer shown/edited anywhere, so
      // it's auto-derived here rather than collected from the form.
      await api.post('/api/credit-packages', { label: `₹${priceInr} Package`, priceInr, creditsAmount: priceInr, audience: newAudience })
      setNewPrice(''); setNewAudience('both'); setAdding(false)
      fetchPackages()
      showToast('Package added.')
    } catch (err) {
      showToast(err.message || 'Failed to add package.', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function handleUpdate(pkg, updates) {
    try {
      const updated = await api.patch(`/api/credit-packages/${pkg._id}`, updates)
      setPackages(prev => prev.map(p => p._id === pkg._id ? updated : p))
    } catch (err) {
      showToast(err.message || 'Failed to update package.', 'error')
    }
  }

  async function handleDelete(pkg) {
    setDeleting(true)
    try {
      await api.delete(`/api/credit-packages/${pkg._id}`)
      setPackages(prev => prev.filter(p => p._id !== pkg._id))
      showToast('Package deleted.')
      setDeleteTarget(null)
    } catch (err) {
      showToast(err.message || 'Failed to delete package.', 'error')
    } finally {
      setDeleting(false)
    }
  }

  function startEdit(pkg) {
    setEditingId(pkg._id)
    setEditPrice(String(pkg.priceInr))
  }

  async function handleSaveEdit(pkg) {
    const priceInr = Number(editPrice)
    if (!priceInr) return
    setEditSaving(true)
    try {
      // creditsAmount always equals priceInr — see handleAdd. label re-derived
      // from the new price too, so it stays consistent even though hidden.
      const updated = await api.patch(`/api/credit-packages/${pkg._id}`, { label: `₹${priceInr} Package`, priceInr, creditsAmount: priceInr })
      setPackages(prev => prev.map(p => p._id === pkg._id ? updated : p))
      setEditingId(null)
      showToast('Package updated.')
    } catch (err) {
      showToast(err.message || 'Failed to update package.', 'error')
    } finally {
      setEditSaving(false)
    }
  }

  if (!packages) return <div className="card p-6 text-sm text-[var(--text-3)] mt-6">Loading…</div>

  return (
    <div className="card p-6 mt-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Credit Packages</p>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="btn-primary text-xs flex-shrink-0">+ Add Package</button>
        )}
      </div>

      {adding && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4 p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
          {/* Label input removed — buyer-facing card shows only price now,
              so there's nothing for a superadmin-set label to feed into.
          <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (e.g. ₹1,000 Pack)"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} /> */}
          <input type="number" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="Price (₹)"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <AudienceSelect value={newAudience} onChange={e => setNewAudience(e.target.value)} size="lg" full />
          <div className="flex gap-2">
            <button onClick={handleAdd} disabled={saving} className="btn-primary text-xs flex-1 justify-center">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setAdding(false)} className="btn-ghost text-xs flex-1 justify-center">Cancel</button>
          </div>
        </div>
      )}

      {packages.length === 0 ? (
        <p className="text-sm text-center py-6" style={{ color: 'var(--text-3)' }}>No packages yet — add one above.</p>
      ) : (
        <div className="space-y-2">
          {packages.map(pkg => (
            editingId === pkg._id ? (
              <div key={pkg._id} className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-3 rounded-xl"
                   style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                {/* Label input removed — see the "Add Package" form above.
                <input value={editLabel} onChange={e => setEditLabel(e.target.value)} placeholder="Label"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} /> */}
                <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} placeholder="Price (₹)"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <div className="flex gap-2">
                  <button onClick={() => handleSaveEdit(pkg)} disabled={editSaving} className="btn-primary text-xs flex-1 justify-center">{editSaving ? 'Saving…' : 'Save'}</button>
                  <button onClick={() => setEditingId(null)} className="btn-ghost text-xs flex-1 justify-center">Cancel</button>
                </div>
              </div>
            ) : (
              <div key={pkg._id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl"
                   style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', opacity: pkg.active ? 1 : 0.5 }}>
                <div className="flex-1 min-w-[140px]">
                  {/* <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{pkg.label}</p> */}
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>₹{pkg.priceInr}</p>
                </div>
                <AudienceSelect value={pkg.audience || 'both'} onChange={e => handleUpdate(pkg, { audience: e.target.value })} />
                <button onClick={() => startEdit(pkg)}
                  className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                  Edit
                </button>
                <button onClick={() => handleUpdate(pkg, { active: !pkg.active })}
                  className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                  style={pkg.active
                    ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                    : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                  {pkg.active ? 'Active' : 'Inactive'}
                </button>
                <button onClick={() => setDeleteTarget(pkg)}
                  className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                  style={{ background: 'rgba(248,113,113,0.12)', color: '#f87171' }}>
                  Delete
                </button>
              </div>
            )
          ))}
        </div>
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Package"
          message={`Delete the ₹${deleteTarget?.priceInr} package? This cannot be undone.`}
          confirmLabel={deleting ? 'Deleting…' : 'Delete'}
          cancelLabel="Cancel"
          danger
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

// Clamps live as the superadmin types, rather than only rejecting on submit —
// blank stays blank (so the field can still be cleared/retyped), any actual
// number gets pinned into [1, 50].
function clampDiscountPct(raw) {
  if (raw === '') return ''
  const n = Number(raw)
  if (Number.isNaN(n)) return raw
  return String(Math.min(50, Math.max(1, n)))
}

function VouchersPanel({ showToast }) {
  const [vouchers, setVouchers] = useState(null)
  const [adding,   setAdding]   = useState(false)
  const [newName,           setNewName]           = useState('')
  const [newPrice,          setNewPrice]          = useState('')
  const [newDiscountPct,    setNewDiscountPct]    = useState('')
  const [newValidFrom,      setNewValidFrom]      = useState('')
  const [newExpiresAt,      setNewExpiresAt]      = useState('')
  const [newMaxRedemptions, setNewMaxRedemptions] = useState('')
  const [saving,  setSaving]  = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [deleting,     setDeleting]     = useState(false)
  const [copiedId, setCopiedId] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editName,           setEditName]           = useState('')
  const [editPrice,          setEditPrice]          = useState('')
  const [editDiscountPct,    setEditDiscountPct]    = useState('')
  const [editValidFrom,      setEditValidFrom]      = useState('')
  const [editExpiresAt,      setEditExpiresAt]      = useState('')
  const [editMaxRedemptions, setEditMaxRedemptions] = useState('')
  const [editSaving, setEditSaving] = useState(false)
  const navigate = useNavigate()

  function fetchVouchers() {
    api.get('/api/vouchers')
      .then(setVouchers)
      .catch(() => { showToast('Failed to load vouchers.', 'error'); setVouchers([]) })
  }

  useEffect(() => { fetchVouchers() }, [])

  async function handleAdd() {
    const price = Number(newPrice)
    const discountPct = Number(newDiscountPct)
    const maxRedemptions = Number(newMaxRedemptions)
    if (!newName.trim() || !price || price < 1 || !discountPct || discountPct < 1 || discountPct > 50
      || !newValidFrom || !newExpiresAt || !maxRedemptions || maxRedemptions < 1) return
    setSaving(true)
    try {
      const created = await api.post('/api/vouchers', {
        name: newName.trim(),
        price,
        discountPct,
        validFrom: newValidFrom,
        expiresAt: newExpiresAt,
        maxRedemptions,
      })
      setNewName(''); setNewPrice(''); setNewDiscountPct(''); setNewValidFrom(''); setNewExpiresAt(''); setNewMaxRedemptions(''); setAdding(false)
      fetchVouchers()
      showToast(`Voucher added — code: ${created.code}`)
    } catch (err) {
      showToast(err.message || 'Failed to add voucher.', 'error')
    } finally {
      setSaving(false)
    }
  }

  function startEdit(voucher) {
    setEditingId(voucher._id)
    setEditName(voucher.name)
    setEditPrice(String(voucher.price))
    setEditDiscountPct(String(voucher.discountPct))
    setEditValidFrom(voucher.validFrom ? voucher.validFrom.slice(0, 10) : '')
    setEditExpiresAt(voucher.expiresAt ? voucher.expiresAt.slice(0, 10) : '')
    setEditMaxRedemptions(voucher.maxRedemptions ? String(voucher.maxRedemptions) : '')
  }

  async function handleSaveEdit(voucher) {
    const price = Number(editPrice)
    const discountPct = Number(editDiscountPct)
    const maxRedemptions = Number(editMaxRedemptions)
    if (!editName.trim() || !price || price < 1 || !discountPct || discountPct < 1 || discountPct > 50
      || !editValidFrom || !editExpiresAt || !maxRedemptions || maxRedemptions < 1) return
    setEditSaving(true)
    try {
      const updated = await api.patch(`/api/vouchers/${voucher._id}`, {
        name: editName.trim(),
        price,
        discountPct,
        validFrom: editValidFrom,
        expiresAt: editExpiresAt,
        maxRedemptions,
      })
      setVouchers(prev => prev.map(v => v._id === voucher._id ? updated : v))
      setEditingId(null)
      showToast('Voucher updated.')
    } catch (err) {
      showToast(err.message || 'Failed to update voucher.', 'error')
    } finally {
      setEditSaving(false)
    }
  }

  async function handleToggleActive(voucher) {
    try {
      const updated = await api.patch(`/api/vouchers/${voucher._id}`, { active: !voucher.active })
      setVouchers(prev => prev.map(v => v._id === voucher._id ? updated : v))
    } catch (err) {
      showToast(err.message || 'Failed to update voucher.', 'error')
    }
  }

  async function handleDelete(voucher) {
    setDeleting(true)
    try {
      await api.delete(`/api/vouchers/${voucher._id}`)
      setVouchers(prev => prev.filter(v => v._id !== voucher._id))
      showToast('Voucher deleted.')
      setDeleteTarget(null)
    } catch (err) {
      showToast(err.message || 'Failed to delete voucher.', 'error')
    } finally {
      setDeleting(false)
    }
  }

  async function handleCopyCode(voucher) {
    try {
      await navigator.clipboard.writeText(voucher.code)
      setCopiedId(voucher._id)
      setTimeout(() => setCopiedId(id => id === voucher._id ? null : id), 2000)
    } catch {
      showToast('Could not copy — copy it manually.', 'error')
    }
  }

  // WhatsApp renders *text* as bold; email has no such markup, so `bold`
  // just uppercases there instead — same underlying message either way.
  function voucherOfferMessage(voucher, bold) {
    const chargeAmount = Math.round(voucher.price * (1 - voucher.discountPct / 100) * 100) / 100
    return `Hey! Great news — here's a special SkillAssess credits offer for you.\n\n`
      + `Use code ${bold(voucher.code)} to get ₹${voucher.price} worth of AI credits for just ₹${chargeAmount} — that's ${bold(voucher.discountPct + '% OFF')} the regular price.\n\n`
      + `Just enter the code at checkout when buying credits. It's a great way to top up and keep generating exams without interruption — but this offer won't last forever, so grab it soon!`
  }

  function handleShareWhatsApp(voucher) {
    const message = voucherOfferMessage(voucher, s => `*${s}*`)
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank')
  }

  function handleShareEmail(voucher) {
    const message = voucherOfferMessage(voucher, s => s.toUpperCase())
    const subject = `Special offer: ${voucher.discountPct}% off SkillAssess AI credits`
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`, '_blank')
  }

  if (!vouchers) return <div className="card p-6 text-sm text-[var(--text-3)] mt-6">Loading…</div>

  return (
    <div className="card p-6 mt-6">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: 'var(--brand)' }}>Vouchers</p>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} className="btn-primary text-xs flex-shrink-0">+ Add Voucher</button>
        )}
      </div>

      {adding && (
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-2 mb-4 p-3 rounded-xl" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <input type="number" min="1" value={newPrice} onChange={e => setNewPrice(e.target.value)} placeholder="Price (₹)"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <input type="number" min="1" max="50" value={newDiscountPct} onChange={e => setNewDiscountPct(clampDiscountPct(e.target.value))} placeholder="Discount %"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <input type="date" value={newValidFrom} onChange={e => setNewValidFrom(e.target.value)} max={newExpiresAt || undefined} placeholder="Valid from"
            className="px-3 py-2 rounded-lg text-sm outline-none cursor-pointer" title="Valid from"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <input type="date" value={newExpiresAt} onChange={e => setNewExpiresAt(e.target.value)} min={newValidFrom || undefined} placeholder="Valid till"
            className="px-3 py-2 rounded-lg text-sm outline-none cursor-pointer" title="Valid till"
            style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <input type="number" min="1" value={newMaxRedemptions} onChange={e => setNewMaxRedemptions(e.target.value)} placeholder="User limit"
            className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
          <div className="flex gap-2">
            <button onClick={handleAdd}
              disabled={saving || !newName.trim() || !newPrice || !newDiscountPct || !newValidFrom || !newExpiresAt || !newMaxRedemptions}
              className="btn-primary text-xs flex-1 justify-center disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
            <button onClick={() => setAdding(false)} className="btn-ghost text-xs flex-1 justify-center">Cancel</button>
          </div>
        </div>
      )}

      {vouchers.length === 0 ? (
        <p className="text-sm text-center py-6" style={{ color: 'var(--text-3)' }}>No vouchers yet — add one above.</p>
      ) : (
        <div className="space-y-2">
          {vouchers.map(v => (
            editingId === v._id ? (
              <div key={v._id} className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-7 gap-2 p-3 rounded-xl"
                   style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                <input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <input type="number" min="1" value={editPrice} onChange={e => setEditPrice(e.target.value)} placeholder="Price (₹)"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <input type="number" min="1" max="50" value={editDiscountPct} onChange={e => setEditDiscountPct(clampDiscountPct(e.target.value))} placeholder="Discount %"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <input type="date" value={editValidFrom} onChange={e => setEditValidFrom(e.target.value)} max={editExpiresAt || undefined} placeholder="Valid from"
                  className="px-3 py-2 rounded-lg text-sm outline-none cursor-pointer" title="Valid from"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <input type="date" value={editExpiresAt} onChange={e => setEditExpiresAt(e.target.value)} min={editValidFrom || undefined} placeholder="Valid till"
                  className="px-3 py-2 rounded-lg text-sm outline-none cursor-pointer" title="Valid till"
                  style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <input type="number" min="1" value={editMaxRedemptions} onChange={e => setEditMaxRedemptions(e.target.value)} placeholder="User limit"
                  className="px-3 py-2 rounded-lg text-sm outline-none" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }} />
                <div className="flex gap-2">
                  <button onClick={() => handleSaveEdit(v)}
                    disabled={editSaving || !editName.trim() || !editPrice || !editDiscountPct || !editValidFrom || !editExpiresAt || !editMaxRedemptions}
                    className="btn-primary text-xs flex-1 justify-center disabled:opacity-50">{editSaving ? 'Saving…' : 'Save'}</button>
                  <button onClick={() => setEditingId(null)} className="btn-ghost text-xs flex-1 justify-center">Cancel</button>
                </div>
              </div>
            ) : (
            <div key={v._id} className="flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-xl"
                 style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', opacity: v.active ? 1 : 0.5 }}>
              <div className="flex-1 min-w-[160px]">
                <p className="text-sm font-semibold" style={{ color: 'var(--text-1)' }}>
                  {v.name} · ₹{v.price} credits for ₹{Math.round(v.price * (1 - v.discountPct / 100) * 100) / 100} ({v.discountPct}% off)
                </p>
                <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                  {v.timesRedeemed} used{v.maxRedemptions ? ` / ${v.maxRedemptions}` : ''}
                  {v.validFrom ? ` · valid from ${new Date(v.validFrom).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                  {v.expiresAt ? ` · expires ${new Date(v.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
                </p>
              </div>
              <span className="font-mono text-sm font-bold tracking-widest px-2.5 py-0.5 rounded-lg flex-shrink-0"
                style={{ background: 'var(--surface)', color: 'var(--brand)', border: '1px solid var(--border)' }}>
                {v.code}
              </span>
              <button onClick={() => handleCopyCode(v)} title="Copy code"
                className="h-6 px-1.5 rounded-md flex items-center justify-center gap-1 flex-shrink-0 text-xs transition-all"
                style={copiedId === v._id
                  ? { background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.3)', color: '#34d399' }
                  : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                {copiedId === v._id ? '✓ Copied' : '⎘ Copy'}
              </button>
              <button onClick={() => handleShareWhatsApp(v)} title="Share via WhatsApp"
                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#25d366' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.117.554 4.103 1.523 5.828L.057 23.405a.75.75 0 00.918.918l5.578-1.466A11.942 11.942 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.932 0-3.733-.505-5.288-1.388l-.38-.219-3.932 1.034 1.034-3.932-.219-.38A9.96 9.96 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z"/></svg>
              </button>
              <button onClick={() => handleShareEmail(v)} title="Share via Email"
                className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 transition-all"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: '#f87171' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </button>
              <button onClick={() => navigate(`/superadmin/vouchers/${v._id}/redemptions`)}
                className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                View Redemptions
              </button>
              <button onClick={() => startEdit(v)}
                className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                Edit
              </button>
              <button onClick={() => handleToggleActive(v)}
                className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                style={v.active
                  ? { background: 'rgba(52,211,153,0.12)', color: '#34d399' }
                  : { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>
                {v.active ? 'Active' : 'Inactive'}
              </button>
              <button onClick={() => setDeleteTarget(v)}
                className="text-xs font-medium px-2.5 py-1 rounded-full flex-shrink-0"
                style={{ background: 'rgba(248,113,113,0.12)', color: '#f87171' }}>
                Delete
              </button>
            </div>
            )
          ))}
        </div>
      )}
      {deleteTarget && (
        <ConfirmModal
          title="Delete Voucher"
          message={`Delete voucher "${deleteTarget.name}" (${deleteTarget.code})? This cannot be undone.`}
          confirmLabel={deleting ? 'Deleting…' : 'Delete'}
          cancelLabel="Cancel"
          danger
          onConfirm={() => handleDelete(deleteTarget)}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  )
}

// ── Payments (Razorpay attempts, all statuses) ────────────────────────────────

const PAYMENT_STATUS_STYLES = {
  paid:      { bg: '#dcfce7', color: '#166534', label: 'Paid' },
  failed:    { bg: '#fee2e2', color: '#991b1b', label: 'Failed' },
  created:   { bg: '#fef3c7', color: '#92400e', label: 'Not completed' },
  cancelled: { bg: '#e5e7eb', color: '#374151', label: 'Cancelled' },
}

function PaymentsView({ showToast }) {
  const [data,     setData]     = useState(null)
  const [page,     setPage]     = useState(1)
  const [limit,    setLimit]    = useState(10)
  const [status,   setStatus]   = useState('all')
  const [search,   setSearch]   = useState('')
  const [query,    setQuery]    = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate,   setToDate]   = useState('')
  const [downloadingId, setDownloadingId] = useState(null)

  // Statements exist only for closed months (same rule as the org's own view).
  const currentMonthKey = (() => {
    const ist = new Date(Date.now() + 5.5 * 60 * 60 * 1000)
    return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, '0')}`
  })()

  async function download(id, path, filename) {
    setDownloadingId(id)
    try {
      const url = await api.downloadFile(path)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 1000)
    } catch (err) {
      showToast(err.message || 'Download failed.', 'error')
    } finally {
      setDownloadingId(null)
    }
  }

  // Statement + Invoice for the month the payment landed in (both are
  // per-account, per-month documents, not per-payment).
  const docButtons = p => {
    if (p.status !== 'paid' || !p.period) return null
    const btnStyle = { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }
    const cls = 'w-8 h-8 inline-flex items-center justify-center rounded-lg disabled:opacity-50 hover:opacity-80'
    const stId = `st-${p.id}`, invId = `inv-${p.id}`
    const svg = { className: 'w-4 h-4', viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.7, strokeLinecap: 'round', strokeLinejoin: 'round' }
    return (
      <div className="flex items-center gap-1.5">
        {p.period < currentMonthKey && p.accountId && (
          <button className={cls} style={btnStyle} disabled={downloadingId === stId}
            title={`Download statement (${p.period})`} aria-label={`Download statement ${p.period}`}
            onClick={() => download(stId, `/api/transactions/statement/${p.period}/download?accountId=${p.accountId}`, `Statement-${p.period}.pdf`)}>
            {downloadingId === stId
              ? <span className="text-xs">…</span>
              : <svg {...svg}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>}
          </button>
        )}
        {p.invoiceId && (
          <button className={cls} style={btnStyle} disabled={downloadingId === invId}
            title={`Download invoice ${p.invoiceNumber ?? ''}`.trim()} aria-label="Download invoice"
            onClick={() => download(invId, `/api/invoices/${p.invoiceId}/download`, `${p.invoiceNumber || 'Invoice'}.pdf`)}>
            {downloadingId === invId
              ? <span className="text-xs">…</span>
              : <svg {...svg}><path d="M6 3h12v18l-3-2-3 2-3-2-3 2z" /><path d="M9 8h6M9 12h6" /></svg>}
          </button>
        )}
      </div>
    )
  }

  // Debounce search so each keystroke isn't a request.
  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1) }, 350)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const qs = new URLSearchParams({ page, limit })
    if (status !== 'all') qs.set('status', status)
    if (query)    qs.set('search', query)
    if (fromDate) qs.set('from', fromDate)
    if (toDate)   qs.set('to', toDate)
    api.get(`/api/payments/all?${qs}`)
      .then(setData)
      .catch(() => { showToast('Failed to load payments.', 'error'); setData({ payments: [], total: 0 }) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, query, fromDate, toDate])

  const total      = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / limit))
  const inputStyle = { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }

  const money = n => `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  // Payment ID once Razorpay has one; an unfinished checkout only has an
  // order ID, so fall back to that (marked) rather than showing nothing.
  const idCell = p => p.razorpayPaymentId
    ? p.razorpayPaymentId
    : (p.razorpayOrderId ? `${p.razorpayOrderId} (order)` : '—')

  const detailText = p => {
    if (p.status === 'paid') {
      return `${p.paymentMethodLabel ?? 'Credits granted'}${p.confirmedVia ? ` · via ${p.confirmedVia}` : ''}${p.paidAt ? ` · ${fmtDateTime(p.paidAt)}` : ''}`
    }
    if (p.status === 'failed') return p.failureReason || p.failureCode || 'Payment failed'
    return 'Checkout opened, no payment received'
  }

  const badge = p => {
    const st = PAYMENT_STATUS_STYLES[p.status] ?? PAYMENT_STATUS_STYLES.created
    return <span className="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap" style={{ background: st.bg, color: st.color }}>{st.label}</span>
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative w-full sm:w-56">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
            className="w-full pl-9 pr-8 py-2 rounded-lg text-sm outline-none" style={inputStyle} />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-xs" style={{ color: 'var(--text-3)' }}>✕</button>}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto sm:ml-auto">
          <select value={status} onChange={e => { setStatus(e.target.value); setPage(1) }}
            className="flex-1 sm:flex-none min-w-0 px-3 py-2 rounded-lg text-sm outline-none" style={inputStyle}>
            <option value="all">All statuses</option>
            <option value="paid">Paid</option>
            <option value="failed">Failed</option>
            <option value="created">Not completed</option>
          </select>
          <input type="date" value={fromDate} onChange={e => { setFromDate(e.target.value); setPage(1) }}
            className="flex-1 sm:flex-none min-w-0 px-3 py-2 rounded-lg text-sm outline-none" style={inputStyle} />
          <input type="date" value={toDate} onChange={e => { setToDate(e.target.value); setPage(1) }}
            className="flex-1 sm:flex-none min-w-0 px-3 py-2 rounded-lg text-sm outline-none" style={inputStyle} />
        </div>
      </div>

      {data && total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4 text-sm" style={{ color: 'var(--text-3)' }}>
          <div className="flex flex-wrap items-center gap-2">
            <span>Rows per page</span>
            <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1) }}
              className="px-2 py-1 rounded-lg text-sm outline-none" style={inputStyle}>
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <span>{(page - 1) * limit + 1}–{Math.min(page * limit, total)} of {total}</span>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded-lg disabled:opacity-40" style={inputStyle}>Prev</button>
            <span className="whitespace-nowrap">Page {page} of {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded-lg disabled:opacity-40" style={inputStyle}>Next</button>
          </div>
        </div>
      )}

      {data === null ? (
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : data.payments.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>No payments found.</p>
      ) : (
        <>
          {/* Wide screens: table */}
          <div className="hidden md:block overflow-x-auto rounded-xl" style={{ border: '1px solid var(--border)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs" style={{ color: 'var(--text-3)', background: 'var(--surface)' }}>
                  {['Date', 'Account', 'Amount', 'Credits', 'Status', 'Payment ID', 'Details', 'Documents'].map(h => (
                    <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.payments.map(p => (
                  <tr key={p.id} className="align-top" style={{ borderTop: '1px solid var(--border)' }}>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-2)' }}>{fmtDateTime(p.createdAt)}</td>
                    <td className="px-3 py-2">
                      <div style={{ color: 'var(--text-1)' }}>{p.account?.name ?? '—'}</div>
                      <div className="text-xs break-all" style={{ color: 'var(--text-3)' }}>{p.account?.email}</div>
                      {p.account?.accountCode && <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.account.accountCode}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-1)' }}>
                      {money(p.amount)}
                      {p.voucherCode && <div className="text-xs" style={{ color: 'var(--text-3)' }}>Voucher {p.voucherCode}</div>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap" style={{ color: 'var(--text-2)' }}>
                      {p.credits != null ? Number(p.credits).toLocaleString('en-IN') : '—'}
                    </td>
                    <td className="px-3 py-2">{badge(p)}</td>
                    <td className="px-3 py-2 text-xs font-mono break-all" style={{ color: 'var(--text-3)' }}>{idCell(p)}</td>
                    <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-3)' }}>{detailText(p)}</td>
                    <td className="px-3 py-2">{docButtons(p)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Small screens: cards */}
          <div className="md:hidden flex flex-col gap-3">
            {data.payments.map(p => (
              <div key={p.id} className="rounded-xl p-3 min-w-0" style={{ border: '1px solid var(--border)', background: 'var(--surface)' }}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" style={{ color: 'var(--text-1)' }}>{p.account?.name ?? '—'}</div>
                    <div className="text-xs break-all" style={{ color: 'var(--text-3)' }}>{p.account?.email}</div>
                    {p.account?.accountCode && <div className="text-xs" style={{ color: 'var(--text-3)' }}>{p.account.accountCode}</div>}
                  </div>
                  {badge(p)}
                </div>
                <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 mt-2 text-sm">
                  <span style={{ color: 'var(--text-1)' }}>{money(p.amount)}{p.credits != null ? ` · ${Number(p.credits).toLocaleString('en-IN')} credits` : ''}</span>
                  <span className="text-xs" style={{ color: 'var(--text-3)' }}>{fmtDateTime(p.createdAt)}</span>
                </div>
                {p.voucherCode && <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>Voucher {p.voucherCode}</div>}
                <div className="text-xs font-mono break-all mt-2" style={{ color: 'var(--text-3)' }}>{idCell(p)}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--text-3)' }}>{detailText(p)}</div>
                {p.status === 'paid' && <div className="mt-2">{docButtons(p)}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ── Main SuperAdminPage ───────────────────────────────────────────────────────

export default function SuperAdminPage() {
  const { user }          = useAuth()
  const location          = useLocation()
  // Coming back from a sub-page (e.g. voucher redemptions) via navigate(-1)
  // passes which tab to land on, so "Back" doesn't dump you on the default
  // Organisations tab instead of where you actually were.
  const [tab, setTab]     = useState(location.state?.tab || 'organisations')
  const [selectedOrg, setSelectedOrg] = useState(null)
  const [toast, setToast] = useState(null)

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type })
    setTimeout(() => setToast(null), 3500)
  }

  if (selectedOrg) {
    return (
      <div className="w-full px-6 sm:px-40 py-6 sm:py-10">
        <Toast toast={toast} />
        <OrgDetail org={selectedOrg} onBack={() => setSelectedOrg(null)} onUpdated={setSelectedOrg} showToast={showToast} />
      </div>
    )
  }

  return (
    <div className="w-full px-6 sm:px-40 py-6 sm:py-10 animate-fade-up">
      <Toast toast={toast} />

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b mb-6 overflow-x-auto" style={{ borderColor: 'var(--border)' }}>
        {[
          { key: 'organisations', label: '🏢 Organisations' },
          { key: 'users',        label: '👤 Examinees'     },
          { key: 'exams',        label: '📝 Exams'         },
          { key: 'usage',        label: '📊 Token Usage'   },
          { key: 'payments',     label: '💰 Payments'      },
          { key: 'settings',     label: '⚙️ Settings'      },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setTab(key)}
            className="px-4 py-2 text-sm font-medium transition-all relative flex-shrink-0 whitespace-nowrap"
            style={{ color: tab === key ? 'var(--brand)' : 'var(--text-3)' }}>
            <span className="inline-flex items-center gap-1.5"><span style={{ fontSize: '1.2em', lineHeight: 1 }}>{label.slice(0, 2)}</span><span>{label.slice(2).trim()}</span></span>
            {tab === key && <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ background: 'var(--brand)' }} />}
          </button>
        ))}
      </div>

      {tab === 'organisations' && (
        <OrgsView onSelectOrg={setSelectedOrg} showToast={showToast} />
      )}

      {tab === 'users' && (
        <AllUsersView showToast={showToast} />
      )}

      {tab === 'exams' && (
        <AllExamsView showToast={showToast} />
      )}

      {tab === 'usage' && (
        <AiUsageView />
      )}

      {tab === 'payments' && (
        <PaymentsView showToast={showToast} />
      )}

      {tab === 'settings' && (
        <>
          <PlatformSettingsView showToast={showToast} />
          <CreditPackagesPanel showToast={showToast} />
          <VouchersPanel showToast={showToast} />
        </>
      )}
    </div>
  )
}
